import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import QRCode from "qrcode";
import {
  ArrowLeft, Download, Loader2, LogOut, Search, Users, UserPlus, PhoneCall,
  CheckCircle2, Clock, X, Waves, ChevronRight, History, UserCog, ClipboardList,
  HeartHandshake, Home, TrendingUp, Smile, QrCode, BarChart3, Link2, MailQuestion,
  Printer, MailPlus, Plane, Stethoscope, CalendarCheck2, AlertTriangle, Baby, Eye, EyeOff, Trash2,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";

const STAGES = ["First-Time Visitor", "Follow-Up Initiated", "Returned", "Regular Attender", "Discipleship / NBC", "Connected to Ministry", "Serving / Involved", "Member"];
const STATUSES = ["Active", "Follow-Up Needed", "Temporarily Away", "Inactive", "Relocated", "Visiting Only", "Do Not Contact"];
const CONTACT_TYPES = ["Call", "Text", "Email", "In person", "Visit"];
const OUTCOMES = ["Connected", "No answer", "Left message", "Follow-up requested", "Declined contact"];
const RELATIONSHIPS = ["Visitor", "Regular Attendee", "Member", "Inactive Member", "Former/Transferred Member", "Visiting Member"];
const CARE_STATUSES = ["Absent", "Sick", "Hospitalized", "Travelling", "Bereavement", "Family Situation", "Work / Schedule", "Relocated", "Away at School", "Needs Pastoral Contact", "Other"];

const inputCls = "w-full min-h-[44px] rounded-xl border border-border bg-white px-4 text-sm outline-none focus:border-water focus:ring-2 focus:ring-water/20 transition-[border-color,box-shadow]";

const fmtTime = (iso) => iso ? new Date(iso).toLocaleString("en-CA", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "";
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" }) : "";

function ProfileField({ label, value, warn }) {
  if (!value) return null;
  return (
    <div>
      <p className="text-[11px] text-muted-foreground">{label}</p>
      <p className={`text-sm font-medium ${warn ? "text-ember" : "text-river"}`}>{value}</p>
    </div>
  );
}

function Login({ onLogin, resetToken }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState(resetToken ? "reset" : "login");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      onLogin(data);
    } catch (err) {
      toast.error(apiError(err, "Login failed"));
    } finally {
      setBusy(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setMode("forgotSent");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    if (newPw !== confirmPw) {
      toast.error("Passwords don't match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token: resetToken, password: newPw });
      toast.success("Password updated — sign in with your new password");
      window.history.replaceState({}, "", "/admin");
      setPassword("");
      setMode("login");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const formSubmit = mode === "forgot" ? submitForgot : mode === "reset" ? submitReset : submit;

  return (
    <div className="min-h-screen bg-river-deep flex items-center justify-center px-5 texture-grain" data-testid="admin-login-page">
      <motion.form
        onSubmit={formSubmit}
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="w-full max-w-sm bg-white rounded-3xl p-8 shadow-2xl"
        data-testid="admin-login-form"
      >
        <div className="flex justify-center mb-5"><Logo size={72} /></div>
        <h1 className="font-serif text-2xl text-river text-center mb-1">{mode === "reset" ? "Choose a New Password" : "Leader Login"}</h1>
        <p className="text-xs text-muted-foreground text-center mb-6 font-mono tracking-widest uppercase">LFCC Follow-Up Hub</p>

        {mode === "forgotSent" && (
          <div className="text-center" data-testid="forgot-sent-message">
            <CheckCircle2 className="w-10 h-10 text-water mx-auto mb-3" />
            <p className="text-sm text-river font-semibold mb-1">Check your inbox</p>
            <p className="text-xs text-muted-foreground mb-6">If that email has an account, a reset link is on its way. It stays valid for one hour.</p>
            <button type="button" data-testid="back-to-login-button" onClick={() => setMode("login")} className="w-full min-h-[48px] rounded-full bg-river text-alabaster font-semibold text-sm hover:bg-river-light transition-colors">Back to Sign In</button>
          </div>
        )}

        {mode === "forgot" && (
          <>
            <p className="text-xs text-muted-foreground mb-4">Enter your account email and we'll send you a link to choose a new password.</p>
            <label className="block mb-6">
              <span className="text-sm font-semibold text-river block mb-1.5">Email</span>
              <input data-testid="forgot-email-input" type="email" name="email" autoComplete="username" required className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} />
            </label>
            <button data-testid="forgot-submit-button" disabled={busy} className="w-full min-h-[48px] rounded-full bg-river text-alabaster font-semibold text-sm hover:bg-river-light disabled:opacity-50 transition-colors inline-flex items-center justify-center gap-2">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Email Me a Reset Link
            </button>
            <button type="button" data-testid="back-to-login-button" onClick={() => setMode("login")} className="w-full mt-3 min-h-[44px] rounded-full text-xs font-semibold text-muted-foreground hover:text-river transition-colors">Back to Sign In</button>
          </>
        )}

        {mode === "reset" && (
          <>
            <label className="block mb-4">
              <span className="text-sm font-semibold text-river block mb-1.5">New password</span>
              <input data-testid="reset-password-input" type="password" name="new-password" autoComplete="new-password" required minLength={8} className={inputCls} value={newPw} onChange={(e) => setNewPw(e.target.value)} />
            </label>
            <label className="block mb-6">
              <span className="text-sm font-semibold text-river block mb-1.5">Confirm new password</span>
              <input data-testid="reset-confirm-input" type="password" name="confirm-password" autoComplete="new-password" required minLength={8} className={inputCls} value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} />
            </label>
            <button data-testid="reset-submit-button" disabled={busy} className="w-full min-h-[48px] rounded-full bg-river text-alabaster font-semibold text-sm hover:bg-river-light disabled:opacity-50 transition-colors inline-flex items-center justify-center gap-2">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Save New Password
            </button>
          </>
        )}

        {mode === "login" && (
          <>
        <label className="block mb-4">
          <span className="text-sm font-semibold text-river block mb-1.5">Email</span>
          <input data-testid="admin-email-input" type="email" name="email" id="login-email" autoComplete="username" required className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} />
        </label>
        <label className="block mb-2">
          <span className="text-sm font-semibold text-river block mb-1.5">Password</span>
          <div className="relative">
            <input data-testid="admin-password-input" type={showPw ? "text" : "password"} name="password" id="login-password" autoComplete="current-password" required className={`${inputCls} pr-12`} value={password} onChange={(e) => setPassword(e.target.value)} />
            <button
              type="button"
              data-testid="admin-password-toggle"
              onClick={() => setShowPw(!showPw)}
              aria-label={showPw ? "Hide password" : "Show password"}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full flex items-center justify-center text-muted-foreground hover:text-river hover:bg-parchment transition-colors"
            >
              {showPw ? <EyeOff className="w-4.5 h-4.5" /> : <Eye className="w-4.5 h-4.5" />}
            </button>
          </div>
        </label>
        <div className="text-right mb-4">
          <button type="button" data-testid="forgot-password-link" onClick={() => setMode("forgot")} className="text-xs font-semibold text-water hover:text-river transition-colors">Forgot password?</button>
        </div>
        <button data-testid="admin-login-button" disabled={busy} className="w-full min-h-[48px] rounded-full bg-river text-alabaster font-semibold text-sm hover:bg-river-light disabled:opacity-50 transition-colors inline-flex items-center justify-center gap-2">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Sign In
        </button>
          </>
        )}
        <a href="/" className="block text-center text-xs text-muted-foreground hover:text-river mt-5" data-testid="back-to-site-link">← Back to visitor page</a>
      </motion.form>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, tone = "river", testid }) {
  const tones = { river: "bg-river/5 text-river", ember: "bg-ember/10 text-ember", water: "bg-water/10 text-water" };
  return (
    <div className="bg-white rounded-2xl border border-border p-5" data-testid={testid}>
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-3 ${tones[tone]}`}>
        <Icon className="w-4.5 h-4.5" />
      </div>
      <p className="text-2xl font-serif font-semibold text-river">{value}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
    </div>
  );
}

function Bar({ name, value, max, suffix }) {
  return (
    <div className="flex items-center gap-3 text-sm">
      <span className="w-40 shrink-0 truncate text-foreground/80">{name}</span>
      <div className="flex-1 h-2 rounded-full bg-parchment overflow-hidden">
        <motion.div initial={{ width: 0 }} animate={{ width: `${max ? (value / max) * 100 : 0}%` }} transition={{ duration: 0.8, ease: "easeOut" }} className="h-full bg-gradient-to-r from-water to-river rounded-full" />
      </div>
      <span className="shrink-0 min-w-12 text-right font-mono text-xs text-muted-foreground whitespace-nowrap">{suffix ?? value}</span>
    </div>
  );
}

function SurveyMetrics() {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/admin/survey-stats").then(({ data }) => setS(data)).catch(() => setS(false));
  }, []);

  if (!s) return null;

  const metrics = [
    { icon: Smile, label: "Welcome Score", value: s.avg_welcomed ? `${s.avg_welcomed}/5` : "—", sub: `${s.welcome_high_pct}% felt very/extremely welcomed` },
    { icon: HeartHandshake, label: "Meaningful Connection", value: `${s.connection_pct}%`, sub: "made a real connection" },
    { icon: TrendingUp, label: "Intention to Return", value: `${s.intention_pct}%`, sub: "probably/definitely returning" },
    { icon: Home, label: "Looking for Church Home", value: `${s.looking_pct}%`, sub: "actively looking or possibly" },
    { icon: Users, label: "Actual Return", value: s.linked_first_visitors ? `${s.actual_return_pct}%` : "—", sub: s.linked_first_visitors ? `of ${s.linked_first_visitors} linked first-timers returned` : "no linked surveys yet" },
  ];

  return (
    <div className="mt-8" data-testid="survey-metrics">
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-4">
        <h3 className="font-serif text-2xl text-river">Visitor Experience</h3>
        <p className="text-xs font-mono text-muted-foreground">
          {s.total} response{s.total === 1 ? "" : "s"} · satisfaction {s.avg_overall}/5 · intention vs. retention below
        </p>
      </div>
      {s.total === 0 ? (
        <div className="bg-white rounded-2xl border border-border p-6 text-sm text-muted-foreground">
          No survey responses yet. Visitors can share feedback at the Survey link after submitting their card.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            {metrics.map((m) => (
              <div key={m.label} className="bg-white rounded-2xl border border-border p-4" data-testid={`survey-metric-${m.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <m.icon className="w-4 h-4 text-water mb-2.5" />
                <p className="text-xl font-serif font-semibold text-river">{m.value}</p>
                <p className="text-[11px] font-semibold text-foreground/80 mt-0.5">{m.label}</p>
                <p className="text-[11px] text-muted-foreground">{m.sub}</p>
              </div>
            ))}
          </div>
          {s.uncomfortable_count > 0 && (
            <p className="text-xs text-ember mt-4 font-medium" data-testid="uncomfortable-flag">
              {s.uncomfortable_count} response{s.uncomfortable_count === 1 ? "" : "s"} mentioned something uncomfortable — review the Surveys tab.
            </p>
          )}
          {s.not_returned_reasons?.length > 0 && (
            <div className="mt-4 bg-white rounded-2xl border border-border p-5" data-testid="not-returned-breakdown">
              <p className="text-sm font-semibold text-river mb-3">Why some haven't returned yet ({s.not_returned} response{s.not_returned === 1 ? "" : "s"})</p>
              <div className="space-y-2.5">
                {s.not_returned_reasons.map((r) => <Bar key={r.name} name={r.name} value={r.count} max={s.not_returned_reasons[0].count} />)}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SurveysView() {
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get("/admin/surveys")
      .then(({ data }) => setItems(data))
      .catch((e) => { toast.error(apiError(e)); setItems([]); });
  }, []);

  if (items === null) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-river" /></div>;

  const typeLabel = { first: "First visit", return: "Return visit", not_returned: "Not returned" };
  const typeCls = { first: "bg-ember/10 text-ember", return: "bg-water/10 text-water", not_returned: "bg-river/10 text-river" };

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} data-testid="surveys-view">
      <p className="text-xs text-muted-foreground mb-5">Visitor Experience Survey responses — newest first. Linked responses show the person's reference.</p>
      {items.length === 0 && <p className="text-sm text-muted-foreground py-8 text-center bg-white rounded-2xl border border-border">No survey responses yet.</p>}
      <div className="space-y-2.5" data-testid="surveys-list">
        {items.map((s) => (
          <div key={s.id} data-testid={`survey-row-${s.id}`} className="bg-white rounded-2xl border border-border p-4">
            <div className="flex items-center gap-2 flex-wrap mb-1.5">
              <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full ${typeCls[s.visit_type] || typeCls.first}`}>
                {typeLabel[s.visit_type] || s.visit_type}
              </span>
              <span className="text-sm font-semibold text-river">{s.person_name || "Anonymous"}</span>
              {s.person_code && <span className="text-xs font-mono text-muted-foreground">{s.person_code}</span>}
              <span className="text-[11px] font-mono text-muted-foreground ml-auto">{fmtDate(s.created_at)}</span>
            </div>
            {s.visit_type === "not_returned" ? (
              <p className="text-xs text-muted-foreground">Reasons: <b className="text-foreground/80">{(s.not_returned_reasons || []).join(", ") || "—"}</b></p>
            ) : (
              <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-muted-foreground">
                <span>Experience: <b className="text-foreground/80">{s.overall_experience}</b></span>
                <span>Felt: <b className="text-foreground/80">{s.welcomed}</b></span>
                <span>Return: <b className="text-foreground/80">{s.likely_return}</b></span>
                <span>Connection: <b className="text-foreground/80">{s.connection}</b></span>
                <span>Follow-up: <b className="text-foreground/80">{s.followup_wanted}</b></span>
              </div>
            )}
            {s.uncomfortable === "Yes" && s.uncomfortable_detail && (
              <p className="text-xs text-ember mt-2 bg-ember/5 border border-ember/20 rounded-lg px-3 py-2">Barrier: "{s.uncomfortable_detail}"</p>
            )}
            {s.one_thing_better && (
              <p className="text-xs text-foreground/70 italic mt-2">"For leadership: {s.one_thing_better}"</p>
            )}
          </div>
        ))}
      </div>
    </motion.div>
  );
}

const HOUSEHOLD_CLASSES = {
  kids_only: { label: "Kids in Ministry — Parents Not Attending", cls: "bg-ember/10 text-ember" },
  not_attending: { label: "Not Attending Recently", cls: "bg-destructive/10 text-destructive" },
  partial: { label: "Partially Engaged", cls: "bg-water/10 text-water" },
  fully_connected: { label: "Fully Connected", cls: "bg-river/10 text-river" },
};

function HouseholdReport({ onOpen }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    api.get("/admin/household-report")
      .then(({ data }) => setData(data))
      .catch((e) => { toast.error(apiError(e)); setData(false); });
  }, []);

  if (data === null) return null;
  if (data === false) return null;

  const order = ["kids_only", "partial", "not_attending", "fully_connected"];

  return (
    <div className="mt-8 pt-6 border-t border-border/60" data-testid="household-report">
      <h3 className="font-serif text-xl text-river mb-1">Household Engagement</h3>
      <p className="text-xs text-muted-foreground mb-5">Fully connected homes versus partially engaged ones — including children in ministry whose parents aren't attending.</p>
      {data.households.length === 0 && <p className="text-sm text-muted-foreground py-4">No households yet — they form automatically when families register children, or create them from any person's profile.</p>}
      {data.households.length > 0 && (
        <>
          <div className="flex flex-wrap gap-2 mb-5">
            {order.map((k) => (
              <span key={k} className={`text-[11px] font-mono uppercase tracking-wider px-3 py-1.5 rounded-full ${HOUSEHOLD_CLASSES[k].cls}`} data-testid={`hh-count-${k}`}>
                {HOUSEHOLD_CLASSES[k].label}: {data.counts[k]}
              </span>
            ))}
          </div>
          <div className="space-y-2.5">
            {data.households.map((h) => (
              <div key={h.id} className="rounded-xl border border-border" data-testid={`hh-row-${h.id}`}>
                <button onClick={() => setOpen(open === h.id ? null : h.id)} className="w-full text-left flex items-center gap-3 px-4 py-3.5" data-testid={`hh-toggle-${h.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-river text-sm">{h.name}</p>
                      <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full ${HOUSEHOLD_CLASSES[h.classification].cls}`}>{HOUSEHOLD_CLASSES[h.classification].label}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {h.adults.length} adult{h.adults.length === 1 ? "" : "s"}
                      {h.children_count > 0 && ` · ${h.children_count} child${h.children_count === 1 ? "" : "ren"} in ministry (${h.children_names.join(", ")})`}
                      {h.any_serving && " · someone serving"}
                    </p>
                  </div>
                  <ChevronRight className={`w-4 h-4 text-muted-foreground shrink-0 transition-transform ${open === h.id ? "rotate-90" : ""}`} />
                </button>
                <AnimatePresence>
                  {open === h.id && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                      <div className="px-4 pb-4 pt-1 space-y-1.5 border-t border-border/60">
                        {h.adults.map((a) => (
                          <button key={a.id} data-testid={`hh-adult-${a.person_id}`} onClick={() => onOpen(a.id)} className="w-full text-left flex items-center justify-between rounded-lg px-3 py-2 hover:bg-parchment/60 transition-colors">
                            <div>
                              <p className="text-sm font-medium text-river">{a.name} <span className="text-[10px] font-mono text-muted-foreground">{a.person_type} · {a.stage_label}</span></p>
                              <p className="text-[11px] text-muted-foreground">
                                {a.visits} visit{a.visits === 1 ? "" : "s"}
                                {a.last_visit ? ` · last attended ${a.last_visit}` : " · no attendance recorded"}
                                {a.serving && " · serving"}
                              </p>
                            </div>
                            {a.days_since !== null && a.days_since > 60 && <span className="text-[10px] font-mono text-ember shrink-0">{a.days_since}d away</span>}
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function MHSection({ icon: Icon, title, sub, testid, children }) {
  return (
    <section data-testid={testid} className="border-t border-border/60 pt-5 mt-6 first:border-0 first:pt-0 first:mt-0">
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4 text-water" />
        <h4 className="font-semibold text-river text-sm">{title}</h4>
      </div>
      <p className="text-[11px] text-muted-foreground mb-3">{sub}</p>
      {children}
    </section>
  );
}

function MinistryHealth() {
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/admin/ministry-health")
      .then(({ data }) => setD(data))
      .catch((e) => { toast.error(apiError(e)); setD(false); });
  }, []);

  if (d === null) return <div className="flex justify-center py-10 bg-white rounded-2xl border border-border"><Loader2 className="w-6 h-6 animate-spin text-river" /></div>;
  if (!d) return null;

  const { attendance: att, acquisition: acq, retention: ret, followup: fu, membership: mem } = d;
  const maxYear = Math.max(...att.by_year.map((r) => r.visits), 1);
  const maxAcq = Math.max(...acq.by_year.map((r) => r.count), 1);
  const maxMem = Math.max(...mem.by_year.map((r) => r.count), 1);
  const peakMonths = [...att.by_month].sort((a, b) => b.visits - a.visits).slice(0, 2).filter((m) => m.visits > 0);
  const firstSunday = att.sunday_ordinals[0];

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="bg-white rounded-2xl border border-border p-6" data-testid="ministry-health">
      <h3 className="font-serif text-xl text-river mb-1">Ministry Health — Unified Report</h3>
      <p className="text-xs text-muted-foreground">
        One view across attendance, visitor acquisition, follow-up effectiveness, repeat attendance and membership growth.
        Trends span the full imported history (2020–2026); effectiveness figures count from launch.
      </p>

      <MHSection icon={BarChart3} title="Church Attendance" sub="Every recorded visit, imported history plus new — by year." testid="mh-attendance">
        <div className="space-y-2">
          {att.by_year.map((r) => <Bar key={r.year} name={r.year} value={r.visits} max={maxYear} />)}
        </div>
        <p className="text-[11px] text-muted-foreground mt-3" data-testid="mh-attendance-insights">
          {peakMonths.length > 0 && <>Peak months: <span className="font-semibold text-river">{peakMonths.map((m) => m.month).join(" & ")}</span> · </>}
          {firstSunday && firstSunday.visits > 0 && <>First Sundays draw <span className="font-semibold text-river">{firstSunday.pct}%</span> of dated visits · </>}
          {att.undated > 0 && <>{att.undated} historical entries had no date and are excluded from trends.</>}
        </p>
      </MHSection>

      <MHSection icon={UserPlus} title="Visitor Acquisition" sub="New people first recorded each year — how many came." testid="mh-acquisition">
        <div className="space-y-2">
          {acq.by_year.map((r) => <Bar key={r.year} name={r.year} value={r.count} max={maxAcq} />)}
        </div>
        <p className="text-[11px] text-muted-foreground mt-3">2020 includes historical records whose original sheets carried no reliable date.</p>
      </MHSection>

      <MHSection icon={TrendingUp} title="Repeat Attendance" sub="Of each year's newcomers, how many came back — how well we retain." testid="mh-retention">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-3">
          <span className={`font-serif text-4xl ${ret.overall_return_pct !== null && ret.overall_return_pct >= (ret.goal_pct || 25) ? "text-water" : "text-ember"}`} data-testid="mh-retention-overall">{ret.overall_return_pct ?? "—"}%</span>
          <span className="text-xs text-muted-foreground">overall return rate across {acq.total} recorded people</span>
          <span className="text-[11px] font-semibold rounded-full border border-border px-3 py-1 text-muted-foreground" data-testid="mh-retention-goal">
            Goal: {ret.goal_pct || 25}%{ret.overall_return_pct !== null && (ret.overall_return_pct < (ret.goal_pct || 25) ? ` · ${(ret.goal_pct || 25) - ret.overall_return_pct} points to go` : " · reached")}
          </span>
        </div>
        <div className="space-y-2">
          {ret.cohorts.map((c) => (
            <Bar key={c.year} name={c.year} value={c.returned} max={c.first_timers} suffix={`${c.returned}/${c.first_timers} · ${c.pct}%`} />
          ))}
        </div>
        {ret.overall_return_pct !== null && ret.overall_return_pct < 20 && (
          <p className="text-[11px] text-ember mt-3" data-testid="mh-retention-warning">A return rate under 20% points to follow-up and connection gaps after the first visit — the follow-up workflow below is built to close this.</p>
        )}
      </MHSection>

      <MHSection icon={PhoneCall} title="Follow-Up Effectiveness" sub="Post-launch data only — what happens after a first visit." testid="mh-followup">
        {fu.completed === 0 && fu.outstanding === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="mh-followup-empty">
            No post-launch follow-ups yet — this fills in as new visitors arrive and leaders record contact.
            {fu.historical_notes > 0 && <> {fu.historical_notes} historical notes are preserved inside people's profiles.</>}
          </p>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
              {[
                { label: "Follow-ups logged", value: fu.completed, testid: "mh-fu-completed" },
                { label: "People contacted", value: fu.people_contacted, testid: "mh-fu-contacted" },
                { label: "Still awaiting contact", value: fu.outstanding, testid: "mh-fu-outstanding" },
                { label: "Avg days to first contact", value: fu.avg_days_to_first ?? "—", testid: "mh-fu-avg-days" },
              ].map((c) => (
                <div key={c.label} className="rounded-xl bg-parchment/60 px-3 py-2.5" data-testid={c.testid}>
                  <p className="font-serif text-2xl text-river">{c.value}</p>
                  <p className="text-[10px] text-muted-foreground">{c.label}</p>
                </div>
              ))}
            </div>
            {fu.outcomes.length > 0 && (
              <div className="space-y-2">
                {fu.outcomes.map((o) => <Bar key={o.name} name={o.name} value={o.count} max={fu.outcomes[0].count} />)}
              </div>
            )}
          </>
        )}
      </MHSection>

      <MHSection icon={Users} title="Membership Growth" sub="Formal members (Right Hand of Fellowship) by year first recorded." testid="mh-membership">
        <div className="flex items-baseline gap-4 mb-3">
          <div className="flex items-baseline gap-2">
            <span className="font-serif text-4xl text-river" data-testid="mh-membership-total">{mem.total_members}</span>
            <span className="text-xs text-muted-foreground">members on record</span>
          </div>
          <span className={`text-xs ${mem.rhf_confirmed === 0 ? "text-ember" : "text-water"} font-semibold`} data-testid="mh-membership-rhf">
            {mem.rhf_confirmed} confirmed via RHF in-app
          </span>
        </div>
        <div className="space-y-2">
          {mem.by_year.map((r) => <Bar key={r.year} name={r.year} value={r.count} max={maxMem} />)}
        </div>
        {mem.imported_members > 0 && (
          <p className="text-[11px] text-muted-foreground mt-3">{mem.imported_members} of these came from the historical workbook — confirm each one's RHF status on their profile as you connect with them.</p>
        )}
      </MHSection>
    </motion.div>
  );
}

function ReportsView({ onOpen }) {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    api.get("/admin/monthly", { params: { months: 6 } })
      .then(({ data }) => setRows(data))
      .catch((e) => { toast.error(apiError(e)); setRows([]); });
  }, []);

  if (rows === null) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-river" /></div>;

  return (
    <div className="space-y-6">
      <MinistryHealth />
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="bg-white rounded-2xl border border-border p-6" data-testid="reports-view">
      <h3 className="font-serif text-xl text-river mb-1">Monthly Leadership Report</h3>
      <p className="text-xs text-muted-foreground mb-5">Satisfaction, intention and retention — month by month. Compare "intends to return" with "actually returned".</p>
      {rows.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">No data yet — reports appear once visitors start coming.</p>}
      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]" data-testid="monthly-table">
            <thead>
              <tr className="text-left text-[11px] font-mono uppercase tracking-wider text-muted-foreground border-b border-border">
                <th className="py-2.5 pr-4">Month</th>
                <th className="py-2.5 pr-4">First-Time</th>
                <th className="py-2.5 pr-4">Returning</th>
                <th className="py-2.5 pr-4">Follow-Ups</th>
                <th className="py-2.5 pr-4">Welcome Score</th>
                <th className="py-2.5 pr-4">Intends to Return</th>
                <th className="py-2.5 pr-4">Actually Returned</th>
                <th className="py-2.5">Surveys</th>
              </tr>
            </thead>
            <tbody>
              {[...rows].reverse().map((r) => (
                <tr key={r.month} data-testid={`month-row-${r.month}`} className="border-b border-border/60 last:border-0">
                  <td className="py-3 pr-4 font-semibold text-river">{r.label}</td>
                  <td className="py-3 pr-4">{r.first_time}</td>
                  <td className="py-3 pr-4">{r.returning}</td>
                  <td className="py-3 pr-4">{r.followups}</td>
                  <td className="py-3 pr-4">{r.welcome_avg ? `${r.welcome_avg}/5` : "—"}</td>
                  <td className="py-3 pr-4">{r.intention_pct !== null ? `${r.intention_pct}%` : "—"}</td>
                  <td className="py-3 pr-4">
                    {r.actual_return_pct !== null ? (
                      <span className={r.intention_pct !== null && r.actual_return_pct < r.intention_pct - 20 ? "text-ember font-semibold" : "text-water font-semibold"}>
                        {r.actual_return_pct}%
                      </span>
                    ) : "—"}
                  </td>
                  <td className="py-3">{r.surveys}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {rows.length > 0 && (
        <p className="text-[11px] text-muted-foreground mt-4">Amber return figures mean far fewer people came back than said they would — investigate follow-up, relationships and post-visit barriers.</p>
      )}
      <HouseholdReport onOpen={onOpen} />
    </motion.div>
    </div>
  );
}

function QrCard({ title, description, path, filename, testid }) {
  const [dataUrl, setDataUrl] = useState("");

  useEffect(() => {
    QRCode.toDataURL(`${window.location.origin}${path}`, {
      width: 640, margin: 2,
      color: { dark: "#0F2C59", light: "#FDFBF7" },
    }).then(setDataUrl).catch(() => {});
  }, [path]);

  return (
    <div className="bg-white rounded-2xl border border-border p-6 text-center" data-testid={testid}>
      <h3 className="font-serif text-xl text-river mb-1">{title}</h3>
      <p className="text-xs text-muted-foreground mb-5">{description}</p>
      {dataUrl ? (
        <img src={dataUrl} alt={`QR code for ${title}`} className="w-48 h-48 mx-auto rounded-xl border border-border" data-testid={`${testid}-image`} />
      ) : (
        <div className="w-48 h-48 mx-auto rounded-xl border border-border flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-river" /></div>
      )}
      <p className="text-[11px] font-mono text-muted-foreground mt-3 break-all">{typeof window !== "undefined" ? window.location.origin : ""}{path}</p>
      {dataUrl && (
        <a href={dataUrl} download={filename} data-testid={`${testid}-download`} className="inline-flex items-center gap-2 mt-4 min-h-[44px] px-6 rounded-full bg-river text-alabaster text-sm font-semibold hover:bg-river-light transition-colors">
          <Download className="w-4 h-4" /> Download PNG
        </a>
      )}
    </div>
  );
}

function PrintView() {
  const printCard = () => {
    const cardQr = document.querySelector('[data-testid="qr-card-image"]')?.src || "";
    const surveyQr = document.querySelector('[data-testid="qr-survey-image"]')?.src || "";
    if (!cardQr || !surveyQr) {
      toast.error("QR codes are still generating — try again in a moment");
      return;
    }
    const crest = `${window.location.origin}/lfcc-crest.png`;
    const win = window.open("", "_blank");
    win.document.write(`<!DOCTYPE html><html><head><title>LFCC Visitor Card</title>
      <style>
        @page { size: 5in 7in; margin: 0; }
        * { margin: 0; box-sizing: border-box; }
        body { font-family: Georgia, serif; color: #0F2C59; }
        .card { width: 5in; height: 3.4in; padding: 0.28in; display: flex; align-items: center; gap: 0.25in; border-bottom: 1px dashed #aaa; page-break-inside: avoid; }
        .card img.crest { width: 0.95in; }
        .card h1 { font-size: 15pt; line-height: 1.25; }
        .card p { font-size: 8.5pt; color: #5A6578; margin-top: 4pt; font-family: Arial, sans-serif; }
        .card img.qr { width: 1.05in; }
        .qrside { text-align: center; font-family: Arial, sans-serif; }
        .qrside span { font-size: 7pt; color: #5A6578; display: block; margin-top: 3pt; }
        .tag { font-size: 7pt; letter-spacing: 2pt; color: #D97706; font-family: Arial, sans-serif; margin-bottom: 4pt; }
      </style></head><body>
      <div class="card">
        <img class="crest" src="${crest}" />
        <div style="flex:1">
          <p class="tag">LIVING FOUNTAIN CHRISTIAN CENTRE</p>
          <h1>Welcome. We're glad you're here.</h1>
          <p>Take a moment to connect with us — it helps us serve you and, only if you choose, stay in touch.</p>
        </div>
        <div class="qrside"><img class="qr" src="${cardQr}" /><span>Prefer digital?<br/>Scan to connect</span></div>
      </div>
      <div class="card">
        <img class="crest" src="${crest}" />
        <div style="flex:1">
          <p class="tag">YOUR FEEDBACK MATTERS</p>
          <h1>How was your visit?</h1>
          <p>"And God shall wipe away all tears from their eyes" — Rev. 7:17<br/>One Person. One Invitation. One Connection.</p>
        </div>
        <div class="qrside"><img class="qr" src="${surveyQr}" /><span>1-minute survey<br/>Scan to share</span></div>
      </div>
      <script>window.onload = () => { setTimeout(() => window.print(), 400); }<\/script>
      </body></html>`);
    win.document.close();
  };

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} data-testid="print-view">
      <div className="bg-white rounded-2xl border border-border p-6 mb-5">
        <h3 className="font-serif text-xl text-river mb-1">QR Codes for Print</h3>
        <p className="text-xs text-muted-foreground">Add these to the physical visitor cards and welcome materials. The codes always point at this site's address — download after deploying to your final domain.</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-5 mb-5">
        <QrCard testid="qr-card" title="Visitor Connection Card" description='"Prefer digital? Scan to connect with LFCC."' path="/" filename="lfcc-connection-card-qr.png" />
        <QrCard testid="qr-survey" title="Experience Survey" description='"Tell us how your visit went — 1 minute."' path="/survey" filename="lfcc-survey-qr.png" />
      </div>
      <div className="bg-white rounded-2xl border border-border p-6" data-testid="printable-card">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h3 className="font-serif text-xl text-river mb-1">Printable Visitor Card</h3>
            <p className="text-xs text-muted-foreground max-w-md">A print-ready 5×7 sheet with two card panels — connection card QR on one, feedback survey QR on the other. Use your browser's "Save as PDF" for a digital copy.</p>
          </div>
          <button onClick={printCard} data-testid="print-card-button" className="inline-flex items-center gap-2 min-h-[44px] px-6 rounded-full bg-ember text-white text-sm font-semibold hover:bg-ember-light transition-colors">
            <Printer className="w-4 h-4" /> Print / Save as PDF
          </button>
        </div>
        <div className="mt-5 border border-dashed border-border rounded-xl p-5 flex items-center gap-5 bg-parchment/40">
          <Logo size={64} />
          <div className="flex-1">
            <p className="text-[10px] font-mono tracking-[0.2em] text-ember">LIVING FOUNTAIN CHRISTIAN CENTRE</p>
            <p className="font-serif text-lg text-river">Welcome. We're glad you're here.</p>
            <p className="text-xs text-muted-foreground">Scan to connect — Rev. 7:17</p>
          </div>
          <MailPlus className="w-6 h-6 text-water" />
        </div>
      </div>
    </motion.div>
  );
}

function PersonDetail({ person, onClose, onChanged, onSwitch }) {
  const [detail, setDetail] = useState(null);
  const [tab, setTab] = useState("followup");
  const [fu, setFu] = useState({ contact_date: new Date().toISOString().slice(0, 10), contact_type: "Call", outcome: "Connected", need_identified: "", referred_to: "", next_action: "", next_follow_up_date: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [care, setCare] = useState({ status: "Absent", expected_return: "", followup_required: false, assigned_to: "", next_follow_up: "", notes: "" });
  const [mem, setMem] = useState(null);
  const [hhQuery, setHhQuery] = useState("");
  const [hhResults, setHhResults] = useState([]);

  useEffect(() => { setDetail(null); setHhQuery(""); setHhResults([]); }, [person.id]);

  const emptyMem = { rhf_completed: false, rhf_date: "", effective_date: "", nbc_completed: "", baptism_status: "", ministry: "", serving: false, notes: "" };

  const load = useCallback(async () => {
    const { data } = await api.get(`/admin/people/${person.id}`);
    setDetail(data);
    setMem({ ...emptyMem, ...(data.membership || {}) });
  }, [person.id]);

  const latestVisit = detail?.visits?.[0] || {};
  const firstSurvey = detail?.surveys?.find((s) => s.visit_type === "first") || detail?.surveys?.[0] || null;
  const todayStr = new Date().toISOString().slice(0, 10);

  const saveMembership = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/people/${person.id}`, { membership: mem });
      toast.success("Membership details saved");
      await load();
      onChanged();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const saveCare = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/people/${person.id}/care`, care);
      toast.success("Care status recorded");
      setCare({ status: "Absent", expected_return: "", followup_required: false, assigned_to: "", next_follow_up: "", notes: "" });
      await load();
      onChanged();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const resolveCare = async (cid) => {
    try {
      await api.patch(`/admin/care/${cid}/resolve`);
      toast.success("Care status resolved");
      await load();
      onChanged();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const createHousehold = async () => {
    try {
      await api.post(`/admin/people/${person.id}/household`, {});
      toast.success("Household created");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const searchHousehold = async () => {
    if (!hhQuery.trim()) return;
    try {
      const { data } = await api.get("/admin/people", { params: { q: hhQuery } });
      setHhResults(data.filter((p) => p.id !== person.id).slice(0, 5));
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const addMember = async (mid) => {
    try {
      await api.post(`/admin/households/${detail.household.id}/members`, { person_id: mid });
      toast.success("Added to household");
      setHhQuery("");
      setHhResults([]);
      await load();
      onChanged();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const removeMember = async (mid) => {
    try {
      await api.delete(`/admin/households/${detail.household.id}/members/${mid}`);
      toast.success("Removed from household");
      await load();
      onChanged();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  useEffect(() => { load(); }, [load]);

  const saveFollowUp = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/people/${person.id}/followups`, fu);
      toast.success("Follow-up recorded");
      setFu({ ...fu, notes: "", next_action: "", need_identified: "", referred_to: "" });
      await load();
      onChanged();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const update = async (patch) => {
    try {
      await api.patch(`/admin/people/${person.id}`, patch);
      await load();
      onChanged();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const completeReferral = async (rid) => {
    try {
      await api.patch(`/admin/referrals/${rid}`);
      await load();
      onChanged();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const copyRecontactLink = async () => {
    const link = `${window.location.origin}/survey?ref=${detail.person_id}&kind=missyou`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Re-contact survey link copied — send it gently by text or email");
    } catch {
      toast.error(link);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-river-deep/50 backdrop-blur-sm flex justify-end"
      onClick={onClose}
      data-testid="person-detail-overlay"
    >
      <motion.div
        initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
        transition={{ type: "spring", stiffness: 260, damping: 30 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xl bg-background h-full overflow-y-auto shadow-2xl"
        data-testid="person-detail-panel"
      >
        {!detail ? (
          <div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 animate-spin text-river" /></div>
        ) : (
          <div className="p-6 sm:p-8">
            <div className="flex items-start justify-between mb-6">
              <div>
                <p className="text-xs font-mono text-muted-foreground tracking-widest">{detail.person_id}</p>
                <h2 className="font-serif text-3xl text-river" data-testid="detail-person-name">{detail.name}</h2>
                <p className="text-sm text-muted-foreground mt-1">{detail.phone || "—"} · {detail.email || "—"}</p>
              </div>
              <button onClick={onClose} data-testid="detail-close-button" className="w-10 h-10 rounded-full hover:bg-parchment flex items-center justify-center"><X className="w-5 h-5" /></button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
              <label className="block">
                <span className="text-xs font-semibold text-river block mb-1">Church Relationship</span>
                <select data-testid="relationship-select" className={inputCls} value={detail.person_type || "Visitor"} onChange={(e) => update({ person_type: e.target.value })}>
                  {RELATIONSHIPS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-river block mb-1">Connection Stage</span>
                <select data-testid="stage-select" className={inputCls} value={detail.stage} onChange={(e) => update({ stage: Number(e.target.value) })}>
                  {STAGES.map((s, i) => <option key={s} value={i + 1}>{`${i + 1} — ${s}`}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-river block mb-1">Follow-Up Status</span>
                <select data-testid="status-select" className={inputCls} value={detail.status} onChange={(e) => update({ status: e.target.value })}>
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-river block mb-1">Gender</span>
                <select data-testid="gender-select" className={inputCls} value={detail.gender || ""} onChange={(e) => update({ gender: e.target.value })}>
                  <option value="">—</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </label>
            </div>
            {detail.person_type === "Member" && !(detail.membership?.rhf_completed) && (
              <p className="text-xs text-ember bg-ember/5 border border-ember/25 rounded-xl px-4 py-2.5 mb-4" data-testid="rhf-reminder">
                Membership requires the Right Hand of Fellowship — confirm RHF below to make this official.
              </p>
            )}
            {detail.review_excluded && (
              <p className="text-xs text-muted-foreground bg-parchment/60 border border-border rounded-xl px-4 py-2.5 mb-4 flex items-center justify-between gap-3" data-testid="review-excluded-banner">
                <span>Hidden from the Sunday Review list (no longer active).</span>
                <button
                  data-testid="review-restore-button"
                  className="font-semibold text-water hover:text-river transition-colors shrink-0"
                  onClick={async () => {
                    try {
                      await api.post(`/admin/sunday-review/${detail.id}/remove`, { mode: "restore" });
                      toast.success("Restored to Sunday Review");
                      load();
                      onChanged();
                    } catch (e) { toast.error(apiError(e)); }
                  }}
                >
                  Restore
                </button>
              </p>
            )}

            <div className="rounded-2xl border border-border bg-white p-5 mb-6" data-testid="profile-summary">
              <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-3">Profile</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3">
                <ProfileField label="First visit" value={detail.created_at?.slice(0, 10)} />
                <ProfileField label="Age" value={detail.age_range} />
                <ProfileField label="Gender" value={detail.gender} />
                <ProfileField label="New to London" value={latestVisit.visit_reasons?.includes("New to London") ? "Yes" : ""} />
                <ProfileField label="Looking for church home" value={firstSurvey?.looking_for_home || (latestVisit.visit_reasons?.includes("Looking for a church home") ? "Yes" : "")} />
                <ProfileField label="Source" value={detail.primary_source} />
                <ProfileField label="Invited by" value={detail.invited_by} />
                <ProfileField label="Return intention" value={firstSurvey?.likely_return} />
                <ProfileField label="Welcome rating" value={firstSurvey?.welcomed} />
                <ProfileField label="Connection made" value={firstSurvey?.connection === "Yes" ? (firstSurvey.connection_who || "Yes") : firstSurvey?.connection} />
                <ProfileField label="Contact permission" value={detail.contact_permission} />
                <ProfileField label="Preferred contact" value={(detail.preferred_contact_methods || []).join(", ")} />
                <ProfileField label="Last attended" value={detail.last_visit_at?.slice(0, 10)} />
                <ProfileField label="Last contact" value={detail.last_contact_at?.slice(0, 10)} />
                <ProfileField label="Last contacted by" value={detail.last_contacted_by} />
                <ProfileField label="Next follow-up" value={detail.next_follow_up_date} warn={detail.next_follow_up_date && detail.next_follow_up_date < todayStr} />
                <ProfileField label="Children's Ministry" value={detail.children_count ? `Yes — ${detail.children_count} child${detail.children_count === 1 ? "" : "ren"} registered` : ""} />
                <ProfileField label="Address" value={detail.address} />
                <ProfileField label="Record origin" value={detail.imported ? `Imported (${detail.import_source})` : ""} />
              </div>
              {(latestVisit.ministry_interests?.length > 0 || latestVisit.next_steps?.length > 0) && (
                <div className="mt-4 pt-3 border-t border-border/60">
                  <p className="text-[11px] text-muted-foreground mb-2">Interests & next steps</p>
                  <div className="flex flex-wrap gap-1.5">
                    {[...(latestVisit.ministry_interests || []), ...(latestVisit.next_steps || [])].map((i) => (
                      <span key={i} className="text-[11px] bg-water/10 text-water px-2.5 py-1 rounded-full">{i}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {detail.person_type === "Member" && mem && (
              <div className="rounded-2xl border border-river/20 bg-river/5 p-5 mb-6" data-testid="membership-panel">
                <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-3">Membership — Right Hand of Fellowship</p>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <span className="text-xs font-semibold text-river block mb-1">RHF Completed</span>
                    <select data-testid="rhf-completed" className={inputCls} value={mem.rhf_completed ? "yes" : "no"} onChange={(e) => setMem({ ...mem, rhf_completed: e.target.value === "yes" })}>
                      <option value="no">No</option><option value="yes">Yes</option>
                    </select>
                  </label>
                  <label className="block">
                    <span className="text-xs font-semibold text-river block mb-1">RHF Date</span>
                    <input data-testid="rhf-date" type="date" className={inputCls} value={mem.rhf_date} onChange={(e) => setMem({ ...mem, rhf_date: e.target.value })} />
                  </label>
                  <label className="block">
                    <span className="text-xs font-semibold text-river block mb-1">Membership Effective</span>
                    <input data-testid="membership-effective" type="date" className={inputCls} value={mem.effective_date} onChange={(e) => setMem({ ...mem, effective_date: e.target.value })} />
                  </label>
                  <label className="block">
                    <span className="text-xs font-semibold text-river block mb-1">New Believers Class</span>
                    <select data-testid="nbc-completed" className={inputCls} value={mem.nbc_completed} onChange={(e) => setMem({ ...mem, nbc_completed: e.target.value })}>
                      <option value="">—</option><option>Yes</option><option>No</option><option>Not Applicable</option>
                    </select>
                  </label>
                  <label className="block">
                    <span className="text-xs font-semibold text-river block mb-1">Baptism Status</span>
                    <input data-testid="baptism-status" className={inputCls} placeholder="e.g. Baptized 2019" value={mem.baptism_status} onChange={(e) => setMem({ ...mem, baptism_status: e.target.value })} />
                  </label>
                  <label className="block">
                    <span className="text-xs font-semibold text-river block mb-1">Ministry / Department</span>
                    <input data-testid="membership-ministry" className={inputCls} value={mem.ministry} onChange={(e) => setMem({ ...mem, ministry: e.target.value })} />
                  </label>
                  <label className="block">
                    <span className="text-xs font-semibold text-river block mb-1">Serving</span>
                    <select data-testid="membership-serving" className={inputCls} value={mem.serving ? "yes" : "no"} onChange={(e) => setMem({ ...mem, serving: e.target.value === "yes" })}>
                      <option value="no">No</option><option value="yes">Yes</option>
                    </select>
                  </label>
                  <label className="block col-span-2">
                    <span className="text-xs font-semibold text-river block mb-1">Membership Notes</span>
                    <textarea data-testid="membership-notes" rows={2} className={`${inputCls} py-2.5 resize-none`} value={mem.notes} onChange={(e) => setMem({ ...mem, notes: e.target.value })} />
                  </label>
                </div>
                <button data-testid="membership-save" onClick={saveMembership} disabled={busy} className="mt-4 w-full min-h-[44px] rounded-full bg-river text-alabaster text-sm font-semibold hover:bg-river-light disabled:opacity-50 transition-colors inline-flex items-center justify-center gap-2">
                  {busy && <Loader2 className="w-4 h-4 animate-spin" />} Save Membership Details
                </button>
              </div>
            )}

            <div className="rounded-2xl border border-border bg-white p-5 mb-6" data-testid="care-section">
              <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-3">Member Care & Absence</p>
              {detail.care && (
                <div className="rounded-xl border border-ember/30 bg-ember/5 p-4 mb-4 text-sm" data-testid="active-care-banner">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold text-river">{detail.care.status} — known since {detail.care.since}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {detail.care.expected_return && `Expected back ${detail.care.expected_return} · `}
                        {detail.care.assigned_to && `Assigned: ${detail.care.assigned_to} · `}
                        {detail.care.followup_required ? "Follow-up required" : "No follow-up needed"}
                        {detail.care.next_follow_up && ` · Next: ${detail.care.next_follow_up}`}
                      </p>
                    </div>
                    <button
                      data-testid="care-resolve"
                      onClick={() => resolveCare(detail.care_records.find((c) => c.active)?.id)}
                      className="shrink-0 text-xs font-semibold px-3.5 py-2 rounded-full bg-water text-white hover:bg-water/80 transition-colors"
                    >
                      Resolve
                    </button>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs font-semibold text-river block mb-1">Care Status</span>
                  <select data-testid="care-status" className={inputCls} value={care.status} onChange={(e) => setCare({ ...care, status: e.target.value })}>
                    {CARE_STATUSES.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-river block mb-1">Expected Return</span>
                  <input data-testid="care-expected-return" type="date" className={inputCls} value={care.expected_return} onChange={(e) => setCare({ ...care, expected_return: e.target.value })} />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-river block mb-1">Assigned To</span>
                  <input data-testid="care-assigned" className={inputCls} placeholder="e.g. Women's Ministry" value={care.assigned_to} onChange={(e) => setCare({ ...care, assigned_to: e.target.value })} />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-river block mb-1">Next Follow-Up</span>
                  <input data-testid="care-next-followup" type="date" className={inputCls} value={care.next_follow_up} onChange={(e) => setCare({ ...care, next_follow_up: e.target.value })} />
                </label>
              </div>
              <label className="flex items-center gap-2.5 mt-3 min-h-[44px] cursor-pointer" data-testid="care-followup-toggle">
                <input type="checkbox" checked={care.followup_required} onChange={(e) => setCare({ ...care, followup_required: e.target.checked })} className="w-4.5 h-4.5 accent-ember" />
                <span className="text-sm font-medium text-river">Follow-up required</span>
              </label>
              <label className="block mt-2">
                <span className="text-xs font-semibold text-river block mb-1">General notes (no sensitive details)</span>
                <textarea data-testid="care-notes" rows={2} className={`${inputCls} py-2.5 resize-none`} value={care.notes} onChange={(e) => setCare({ ...care, notes: e.target.value })} />
              </label>
              <button data-testid="care-save" onClick={saveCare} disabled={busy} className="mt-4 w-full min-h-[44px] rounded-full bg-ember text-white text-sm font-semibold hover:bg-ember-light disabled:opacity-50 transition-colors inline-flex items-center justify-center gap-2">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} Record Care Status
              </button>
              {detail.care_records?.filter((c) => !c.active).length > 0 && (
                <div className="mt-4 pt-3 border-t border-border/60 space-y-2" data-testid="care-history">
                  <p className="text-[11px] text-muted-foreground">Past care records</p>
                  {detail.care_records.filter((c) => !c.active).map((c) => (
                    <p key={c.id} className="text-xs text-muted-foreground">
                      {c.status} · {c.created_at?.slice(0, 10)} → resolved {c.resolved_at?.slice(0, 10) || "—"} · by {c.created_by}
                    </p>
                  ))}
                </div>
              )}
            </div>

            {detail.needs_recontact && (
              <div className="rounded-xl border border-water/30 bg-water/5 p-4 mb-4 text-sm flex items-center justify-between gap-3" data-testid="recontact-banner">
                <div>
                  <p className="font-semibold text-river inline-flex items-center gap-1.5"><MailQuestion className="w-4 h-4" /> Haven't seen them in 2+ weeks</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Send the gentle "we appreciated having you" survey — research, not pressure.</p>
                </div>
                <button onClick={copyRecontactLink} data-testid="copy-recontact-link" className="shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold px-3.5 py-2 rounded-full bg-river text-alabaster hover:bg-river-light transition-colors">
                  <Link2 className="w-3.5 h-3.5" /> Copy Link
                </button>
              </div>
            )}

            {detail.is_minor && detail.guardian && (
              <div className="rounded-xl border border-water/30 bg-water/5 p-4 mb-4 text-sm">
                <p className="font-semibold text-river">Minor — contact via guardian</p>
                <p className="text-muted-foreground">{detail.guardian.name} · {detail.guardian.phone} · {detail.guardian.email}</p>
              </div>
            )}

            <div className="rounded-2xl border border-border bg-white p-5 mb-6" data-testid="household-section">
              <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-3">Household</p>
              {detail.household ? (
                <>
                  <p className="font-serif text-lg text-river mb-3" data-testid="household-name">{detail.household.name}</p>
                  <div className="space-y-1.5 mb-4">
                    {detail.household.members.map((m) => (
                      <div key={m.id} className="flex items-center gap-2.5 rounded-xl border border-border px-3.5 py-2.5" data-testid={`household-member-${m.person_id}`}>
                        <div className="flex-1 min-w-0">
                          {m.id !== person.id && onSwitch ? (
                            <button onClick={() => onSwitch(m.id)} className="text-sm font-semibold text-river hover:text-water transition-colors" data-testid={`household-switch-${m.person_id}`}>{m.name}</button>
                          ) : (
                            <span className="text-sm font-semibold text-river">{m.name}</span>
                          )}
                          <span className="text-[10px] font-mono text-muted-foreground ml-2">{m.person_type} · {m.stage_label}</span>
                          {m.care_status && <span className="text-[10px] font-mono uppercase tracking-wider bg-ember/10 text-ember px-2 py-0.5 rounded-full ml-1.5">{m.care_status}</span>}
                        </div>
                        {m.id !== person.id && (
                          <button data-testid={`household-remove-${m.person_id}`} onClick={() => removeMember(m.id)} className="text-muted-foreground hover:text-destructive transition-colors"><X className="w-3.5 h-3.5" /></button>
                        )}
                      </div>
                    ))}
                    {detail.household.children.map((c, i) => (
                      <div key={i} className="flex items-center gap-2.5 rounded-xl border border-water/25 bg-water/5 px-3.5 py-2.5" data-testid={`household-child-${i}`}>
                        <Baby className="w-3.5 h-3.5 text-water shrink-0" />
                        <p className="text-sm text-river">{c.name}{c.age ? `, ${c.age}` : ""} <span className="text-[10px] font-mono text-muted-foreground">· Children's Ministry</span></p>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input
                      data-testid="household-search"
                      className={`${inputCls} flex-1`}
                      placeholder="Add family member — search by name…"
                      value={hhQuery}
                      onChange={(e) => setHhQuery(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && searchHousehold()}
                    />
                    <button data-testid="household-search-button" onClick={searchHousehold} className="shrink-0 min-h-[44px] px-4 rounded-full bg-river text-alabaster text-xs font-semibold hover:bg-river-light transition-colors">Find</button>
                  </div>
                  {hhResults.length > 0 && (
                    <div className="mt-2 space-y-1.5" data-testid="household-results">
                      {hhResults.map((r) => (
                        <div key={r.id} className="flex items-center justify-between rounded-xl border border-border px-3.5 py-2">
                          <span className="text-sm text-river">{r.name} <span className="text-[10px] font-mono text-muted-foreground">{r.person_id}</span></span>
                          <button data-testid={`household-add-${r.person_id}`} onClick={() => addMember(r.id)} className="text-xs font-semibold px-3 py-1.5 rounded-full bg-water text-white hover:bg-water/80 transition-colors">Add</button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <p className="text-xs text-muted-foreground mb-3">No household linked. Grouping family members keeps follow-up family-aware — one case per home, not four.</p>
                  <button data-testid="household-create" onClick={createHousehold} className="min-h-[44px] px-5 rounded-full border-2 border-river text-river text-xs font-semibold hover:bg-river hover:text-alabaster transition-colors">
                    Create {detail.name.split(" ").slice(-1)[0]} Family household
                  </button>
                </>
              )}
            </div>

            {detail.referrals.length > 0 && (
              <div className="mb-6" data-testid="referrals-list">
                <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">Referrals / Next Steps</p>
                <div className="space-y-2">
                  {detail.referrals.map((r) => (
                    <div key={r.id} className={`flex items-center justify-between rounded-xl border p-3 text-sm ${r.completed ? "border-border bg-parchment/50 opacity-70" : "border-ember/30 bg-ember/5"}`}>
                      <div>
                        <p className="font-semibold text-river">{r.type}</p>
                        <p className="text-xs text-muted-foreground">→ {r.assigned_to}{r.completed_by ? ` · done by ${r.completed_by}` : ""}</p>
                      </div>
                      {r.completed ? (
                        <span className="inline-flex items-center gap-1 text-xs text-water font-semibold"><CheckCircle2 className="w-3.5 h-3.5" /> Done</span>
                      ) : (
                        <button data-testid={`referral-complete-${r.id}`} onClick={() => completeReferral(r.id)} className="text-xs font-semibold px-3 py-1.5 rounded-full bg-ember text-white hover:bg-ember-light transition-colors">Mark Done</button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {detail.surveys?.length > 0 && (
              <div className="mb-6" data-testid="person-surveys">
                <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">Experience Surveys</p>
                <div className="space-y-2">
                  {detail.surveys.map((s) => (
                    <div key={s.id} className="rounded-xl border border-border bg-white p-3.5 text-sm">
                      <div className="flex justify-between mb-1">
                        <span className="font-semibold text-river">{s.visit_type === "return" ? "Return visit" : s.visit_type === "not_returned" ? "Not returned" : "First visit"}{s.overall_experience ? ` — ${s.overall_experience}` : ""}</span>
                        <span className="text-xs font-mono text-muted-foreground">{fmtDate(s.created_at)}</span>
                      </div>
                      {s.visit_type === "not_returned" ? (
                        <p className="text-xs text-muted-foreground">Reasons: {(s.not_returned_reasons || []).join(", ") || "—"}</p>
                      ) : (
                        <p className="text-xs text-muted-foreground">Felt {(s.welcomed || "").toLowerCase()} · {(s.likely_return || "").toLowerCase()} · connection: {(s.connection || "").toLowerCase()}</p>
                      )}
                      {s.one_thing_better && <p className="text-xs italic text-foreground/70 mt-1.5">"{s.one_thing_better}"</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {detail.visits.map((v) => v.prayer_request).filter(Boolean).slice(0, 1).map((p, i) => (
              <div key={i} className="rounded-xl border border-ember/30 bg-ember/5 p-4 mb-6 text-sm" data-testid="prayer-request-note">
                <p className="font-semibold text-river mb-1">Prayer request — pastoral care only</p>
                <p className="text-foreground/80 italic">"{p}"</p>
              </div>
            ))}

            <div className="flex gap-2 mb-4 border-b border-border">
              {[["followup", "Add Follow-Up"], ["history", `History (${detail.followups.length})`], ["visits", `Visits (${detail.visits.length})`]].map(([id, label]) => (
                <button key={id} data-testid={`detail-tab-${id}`} onClick={() => setTab(id)} className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === id ? "border-ember text-river" : "border-transparent text-muted-foreground hover:text-river"}`}>{label}</button>
              ))}
            </div>

            {tab === "followup" && (
              <div className="space-y-4" data-testid="followup-form">
                <div className="grid grid-cols-2 gap-3">
                  <label className="block"><span className="text-xs font-semibold text-river block mb-1">Contact date</span>
                    <input data-testid="fu-date" type="date" className={inputCls} value={fu.contact_date} onChange={(e) => setFu({ ...fu, contact_date: e.target.value })} /></label>
                  <label className="block"><span className="text-xs font-semibold text-river block mb-1">Contact type</span>
                    <select data-testid="fu-type" className={inputCls} value={fu.contact_type} onChange={(e) => setFu({ ...fu, contact_type: e.target.value })}>
                      {CONTACT_TYPES.map((t) => <option key={t}>{t}</option>)}</select></label>
                </div>
                <label className="block"><span className="text-xs font-semibold text-river block mb-1">Outcome</span>
                  <select data-testid="fu-outcome" className={inputCls} value={fu.outcome} onChange={(e) => setFu({ ...fu, outcome: e.target.value })}>
                    {OUTCOMES.map((o) => <option key={o}>{o}</option>)}</select></label>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block"><span className="text-xs font-semibold text-river block mb-1">Need identified</span>
                    <input data-testid="fu-need" className={inputCls} value={fu.need_identified} onChange={(e) => setFu({ ...fu, need_identified: e.target.value })} /></label>
                  <label className="block"><span className="text-xs font-semibold text-river block mb-1">Referred to</span>
                    <input data-testid="fu-referred" className={inputCls} value={fu.referred_to} onChange={(e) => setFu({ ...fu, referred_to: e.target.value })} /></label>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block"><span className="text-xs font-semibold text-river block mb-1">Next action</span>
                    <input data-testid="fu-next-action" className={inputCls} value={fu.next_action} onChange={(e) => setFu({ ...fu, next_action: e.target.value })} /></label>
                  <label className="block"><span className="text-xs font-semibold text-river block mb-1">Next follow-up date</span>
                    <input data-testid="fu-next-date" type="date" className={inputCls} value={fu.next_follow_up_date} onChange={(e) => setFu({ ...fu, next_follow_up_date: e.target.value })} /></label>
                </div>
                <label className="block"><span className="text-xs font-semibold text-river block mb-1">Brief notes</span>
                  <textarea data-testid="fu-notes" rows={3} className={`${inputCls} py-2.5 resize-none`} value={fu.notes} onChange={(e) => setFu({ ...fu, notes: e.target.value })} /></label>
                <button data-testid="fu-submit" onClick={saveFollowUp} disabled={busy} className="w-full min-h-[48px] rounded-full bg-river text-alabaster font-semibold text-sm hover:bg-river-light disabled:opacity-50 transition-colors inline-flex items-center justify-center gap-2">
                  {busy && <Loader2 className="w-4 h-4 animate-spin" />} Record Follow-Up
                </button>
              </div>
            )}

            {tab === "history" && (
              <div className="space-y-3" data-testid="followup-history">
                {detail.followups.length === 0 && <p className="text-sm text-muted-foreground">No follow-ups recorded yet.</p>}
                {detail.followups.map((f) => (
                  <div key={f.id} className="rounded-xl border border-border bg-white p-4 text-sm">
                    <div className="flex justify-between mb-1">
                      <span className="font-semibold text-river">{f.contact_type} — {f.outcome}</span>
                      <span className="text-xs font-mono text-muted-foreground">{f.contact_date}</span>
                    </div>
                    {f.notes && <p className="text-foreground/80">{f.notes}</p>}
                    {(f.next_action || f.next_follow_up_date) && (
                      <p className="text-xs text-muted-foreground mt-1.5">Next: {f.next_action} {f.next_follow_up_date && `· ${f.next_follow_up_date}`}</p>
                    )}
                    <p className="text-[11px] text-muted-foreground mt-1">entered by {f.recorded_by}</p>
                  </div>
                ))}
              </div>
            )}

            {tab === "visits" && (
              <div className="space-y-3" data-testid="visits-list">
                {detail.visits.map((v) => (
                  <div key={v.id} className="rounded-xl border border-border bg-white p-4 text-sm">
                    <div className="flex justify-between mb-1">
                      <span className="font-semibold text-river">{v.visit_date}</span>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${v.is_first_time === "Yes" ? "bg-ember/10 text-ember" : "bg-water/10 text-water"}`}>{v.is_first_time === "Yes" ? "First time" : "Returning"}</span>
                    </div>
                    {v.visit_reasons.length > 0 && <p className="text-muted-foreground text-xs">Brings them: {v.visit_reasons.join(", ")}</p>}
                    {v.heard_about.length > 0 && <p className="text-muted-foreground text-xs">Source: {v.heard_about.join(", ")}</p>}
                    {v.invited_by && <p className="text-muted-foreground text-xs">Invited by: {v.invited_by}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

function MinistryRouting() {
  const [contacts, setContacts] = useState(null);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/contacts");
      setContacts(data);
      setEdits(Object.fromEntries(data.map((c) => [c.role, c.email])));
    } catch (e) {
      toast.error(apiError(e));
      setContacts([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (role) => {
    setSaving(role);
    try {
      await api.put("/admin/contacts", { role, email: edits[role] || "" });
      toast.success(edits[role] ? `${role} alerts → ${edits[role]}` : `${role} routing cleared`);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving("");
    }
  };

  if (contacts === null) return null;

  return (
    <div className="bg-white rounded-2xl border border-border p-6 mt-6" data-testid="ministry-routing">
      <h3 className="font-serif text-xl text-river mb-1">Ministry Email Routing</h3>
      <p className="text-xs text-muted-foreground mb-5">When a referral is created for one of these roles, that person gets their own email instantly. Leave blank to skip. The main inbox always receives a summary.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        {contacts.map((c) => (
          <div key={c.role} className="flex items-center gap-2" data-testid={`routing-row-${c.role.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <span className="text-xs font-semibold text-river w-40 shrink-0 truncate" title={c.role}>{c.role}</span>
            <input
              className="flex-1 min-h-[40px] rounded-lg border border-border bg-white px-3 text-xs outline-none focus:border-water focus:ring-2 focus:ring-water/20"
              placeholder="leader@email.com"
              data-testid={`routing-input-${c.role.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              value={edits[c.role] ?? ""}
              onChange={(e) => setEdits({ ...edits, [c.role]: e.target.value })}
            />
            <button
              onClick={() => save(c.role)}
              disabled={saving === c.role}
              data-testid={`routing-save-${c.role.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="shrink-0 text-xs font-semibold px-3 py-2 rounded-full bg-river text-alabaster hover:bg-river-light disabled:opacity-50 transition-colors"
            >
              {saving === c.role ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function TeamView({ currentUser }) {
  const [users, setUsers] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "leader" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (e) {
      toast.error(apiError(e));
      setUsers([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/users", form);
      toast.success(`Account created for ${form.name}`);
      setForm({ name: "", email: "", password: "", role: "leader" });
      await load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (u) => {
    try {
      await api.patch(`/admin/users/${u.id}`, { active: !u.active });
      toast.success(u.active ? `${u.name} deactivated` : `${u.name} reactivated`);
      await load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  if (users === null) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-river" /></div>;

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} data-testid="team-view">
      <div className="grid lg:grid-cols-5 gap-6">
      <div className="lg:col-span-3 bg-white rounded-2xl border border-border p-6">
        <h3 className="font-serif text-xl text-river mb-1">Team Accounts</h3>
        <p className="text-xs text-muted-foreground mb-5">Everyone listed can sign in and enter visitor follow-up information. Every entry is stamped with their name.</p>
        <div className="space-y-2.5" data-testid="users-list">
          {users.map((u) => (
            <div key={u.id} data-testid={`user-row-${u.email}`} className={`flex items-center gap-3 rounded-xl border p-3.5 ${u.active ? "border-border" : "border-border bg-parchment/50 opacity-60"}`}>
              <div className="w-10 h-10 rounded-full bg-river/5 flex items-center justify-center font-serif font-semibold text-river shrink-0">
                {(u.name || u.email).split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold text-river text-sm">{u.name || "—"}</p>
                  <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full ${u.role === "admin" ? "bg-ember/10 text-ember" : "bg-water/10 text-water"}`}>
                    {u.role === "admin" ? "Administrator" : "Leader"}
                  </span>
                  {!u.active && <span className="text-[10px] font-mono uppercase tracking-wider bg-destructive/10 text-destructive px-2 py-0.5 rounded-full">Deactivated</span>}
                </div>
                <p className="text-xs text-muted-foreground truncate">{u.email}{u.created_by ? ` · added by ${u.created_by}` : ""}</p>
              </div>
              {u.id !== currentUser.id && (
                <button
                  data-testid={`user-toggle-${u.email}`}
                  onClick={() => toggle(u)}
                  className={`text-xs font-semibold px-3.5 py-2 rounded-full transition-colors ${u.active ? "text-destructive hover:bg-destructive/10" : "text-water hover:bg-water/10"}`}
                >
                  {u.active ? "Deactivate" : "Reactivate"}
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      <form onSubmit={create} className="lg:col-span-2 bg-white rounded-2xl border border-border p-6 self-start" data-testid="create-user-form">
        <h3 className="font-serif text-xl text-river mb-1">Add an Account</h3>
        <p className="text-xs text-muted-foreground mb-5">Administrators can manage accounts; leaders can enter and update visitor information. Referral alert emails go to every active administrator's email — use real inboxes.</p>
        <div className="space-y-4">
          <label className="block"><span className="text-xs font-semibold text-river block mb-1">Full name</span>
            <input data-testid="new-user-name" required className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
          <label className="block"><span className="text-xs font-semibold text-river block mb-1">Email</span>
            <input data-testid="new-user-email" type="email" required className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></label>
          <label className="block"><span className="text-xs font-semibold text-river block mb-1">Temporary password (min. 8 characters)</span>
            <input data-testid="new-user-password" type="text" required minLength={8} className={inputCls} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></label>
          <label className="block"><span className="text-xs font-semibold text-river block mb-1">Role</span>
            <select data-testid="new-user-role" className={inputCls} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
              <option value="leader">Leader — enter follow-ups & referrals</option>
              <option value="children_lead">Children's Ministry Lead — check-in, profiles & incidents</option>
              <option value="children_volunteer">Children's Volunteer — Sunday check-in screen</option>
              <option value="admin">Administrator — full access incl. accounts</option>
            </select></label>
          <button data-testid="create-user-button" disabled={busy} className="w-full min-h-[48px] rounded-full bg-river text-alabaster font-semibold text-sm hover:bg-river-light disabled:opacity-50 transition-colors inline-flex items-center justify-center gap-2">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Create Account
          </button>
        </div>
      </form>
      </div>
      <MinistryRouting />
    </motion.div>
  );
}

function ActivityView() {
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get("/admin/activity")
      .then(({ data }) => setItems(data))
      .catch((e) => { toast.error(apiError(e)); setItems([]); });
  }, []);

  if (items === null) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-river" /></div>;

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="bg-white rounded-2xl border border-border p-6" data-testid="activity-view">
      <h3 className="font-serif text-xl text-river mb-1">Accountability Log</h3>
      <p className="text-xs text-muted-foreground mb-5">Who entered or changed what, and when. Newest first.</p>
      {items.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">No activity recorded yet.</p>}
      <div className="space-y-2.5" data-testid="activity-list">
        {items.map((a) => (
          <div key={a.id} data-testid={`activity-row-${a.id}`} className="flex items-start gap-3.5 rounded-xl border border-border p-3.5">
            <div className="w-9 h-9 rounded-full bg-river/5 flex items-center justify-center font-serif font-semibold text-river text-sm shrink-0">
              {(a.actor_name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm"><span className="font-semibold text-river">{a.actor_name}</span> <span className="text-foreground/80">{a.action.toLowerCase()}</span></p>
              {a.detail && <p className="text-xs text-muted-foreground mt-0.5">{a.detail}</p>}
            </div>
            <span className="text-[11px] font-mono text-muted-foreground shrink-0 mt-0.5">{fmtTime(a.created_at)}</span>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

const NOTIFY_REASONS = [
  ["pickup", "Pickup Requested"],
  ["needs_parent", "Child Needs Parent"],
  ["medical", "Medical / Emergency"],
  ["personal_care", "Bathroom / Personal Care"],
  ["support", "Behavioural / Emotional Support"],
  ["other", "Other"],
];
const INCIDENT_TYPES = ["Injury", "Illness", "Behaviour", "Safety", "Parent Requested", "Other"];

function ChildrenView({ user }) {
  const isLead = ["admin", "children_lead"].includes(user.role);
  const [children, setChildren] = useState(null);
  const [today, setToday] = useState([]);
  const [incidents, setIncidents] = useState(null);
  const [notifyFor, setNotifyFor] = useState(null);
  const [notifyMsg, setNotifyMsg] = useState(null);
  const [checkoutFor, setCheckoutFor] = useState(null);
  const [checkout, setCheckout] = useState({ code: "", released_to: "" });
  const [incidentFor, setIncidentFor] = useState(null);
  const [incident, setIncident] = useState({ type: "Injury", occurred_at: "", staff_present: "", what_occurred: "", action_taken: "", parent_contacted_at: "", parent_response: "", released_to: "", followup_required: false });
  const [profile, setProfile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, t] = await Promise.all([api.get("/admin/children"), api.get("/admin/children/today")]);
      setChildren(c.data);
      setToday(t.data);
      if (isLead) {
        const inc = await api.get("/admin/children/incidents");
        setIncidents(inc.data);
      }
    } catch (e) {
      toast.error(apiError(e));
      setChildren([]);
    }
  }, [isLead]);

  useEffect(() => { load(); }, [load]);

  if (children === null) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-river" /></div>;

  const checkedIn = today.filter((t) => !t.checkout_at);
  const checkedOut = today.filter((t) => t.checkout_at);
  const expected = children.filter((c) => !today.some((t) => t.id === c.id));

  const checkIn = async (id) => {
    try {
      const { data } = await api.post(`/admin/children/${id}/checkin`, {});
      toast.success(`Checked in — pickup code ${data.pickup_code}`);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const doCheckout = async (checkinId) => {
    setBusy(true);
    try {
      await api.post(`/admin/checkins/${checkinId}/checkout`, checkout);
      toast.success("Checked out — time recorded");
      setCheckoutFor(null);
      setCheckout({ code: "", released_to: "" });
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const sendNotify = async (child, reason) => {
    try {
      const { data } = await api.post(`/admin/children/${child.id}/notify`, { reason });
      setNotifyMsg({ childId: child.id, ...data });
      setNotifyFor(null);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const copyNotify = async () => {
    try {
      await navigator.clipboard.writeText(notifyMsg.message);
      toast.success("Message copied — paste into a text to the parent");
    } catch {
      toast.error(notifyMsg.message);
    }
  };

  const saveIncident = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/children/${incidentFor.id}/incidents`, incident);
      toast.success("Incident report filed");
      setIncidentFor(null);
      setIncident({ type: "Injury", occurred_at: "", staff_present: "", what_occurred: "", action_taken: "", parent_contacted_at: "", parent_response: "", released_to: "", followup_required: false });
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const AlertChips = ({ c }) => (
    <span className="inline-flex gap-1.5 flex-wrap">
      {c.allergy_alert && <span className="text-[10px] font-mono uppercase tracking-wider bg-ember/10 text-ember px-2 py-0.5 rounded-full">Allergy</span>}
      {c.dietary_alert && <span className="text-[10px] font-mono uppercase tracking-wider bg-ember/10 text-ember px-2 py-0.5 rounded-full">Dietary</span>}
      {c.support_alert && <span className="text-[10px] font-mono uppercase tracking-wider bg-water/10 text-water px-2 py-0.5 rounded-full">Support</span>}
      {!c.photo_permission && <span className="text-[10px] font-mono uppercase tracking-wider bg-river/10 text-river px-2 py-0.5 rounded-full">No photos</span>}
    </span>
  );

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} data-testid="children-view">
      <div className="grid grid-cols-3 gap-4 mb-6">
        <StatCard testid="children-expected" icon={Baby} label="Expected Today" value={expected.length + checkedIn.length + checkedOut.length} />
        <StatCard testid="children-checked-in" icon={CheckCircle2} label="Checked In" value={checkedIn.length} tone="water" />
        <StatCard testid="children-checked-out" icon={Clock} label="Checked Out" value={checkedOut.length} tone="ember" />
      </div>

      <div className="bg-white rounded-2xl border border-border p-6 mb-5">
        <h3 className="font-serif text-xl text-river mb-1">Today's Children — {checkedIn.length} checked in</h3>
        <p className="text-xs text-muted-foreground mb-4">Parent texts are deliberately discreet — they never contain medical or behavioural details. For a true emergency, use the emergency protocol and call directly.</p>
        {checkedIn.length === 0 && <p className="text-sm text-muted-foreground py-4">Nobody checked in yet this Sunday.</p>}
        <div className="space-y-3" data-testid="checked-in-list">
          {checkedIn.map((c) => (
            <div key={c.checkin_id} className="rounded-xl border border-water/30 bg-water/5 p-4" data-testid={`checked-in-${c.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <p className="font-semibold text-river">
                    {c.name} <span className="text-xs font-mono text-muted-foreground">Age {c.age || "—"}{c.room ? ` · ${c.room}` : ""}</span>
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">Parent: {c.parent_name} · {c.parent_mobile}</p>
                  <div className="mt-1.5"><AlertChips c={c} /></div>
                </div>
                <div className="text-right">
                  <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Pickup code</p>
                  <p className="font-mono text-2xl font-semibold text-ember" data-testid={`pickup-code-${c.id}`}>{c.pickup_code}</p>
                  <p className="text-[11px] text-muted-foreground">in {fmtTime(c.checkin_at)}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                <button data-testid={`notify-${c.id}`} onClick={() => { setNotifyFor(notifyFor === c.id ? null : c.id); setNotifyMsg(null); }} className="text-xs font-semibold px-3.5 py-2 rounded-full bg-river text-alabaster hover:bg-river-light transition-colors">Contact Parent</button>
                {isLead && <button data-testid={`incident-${c.id}`} onClick={() => setIncidentFor(c)} className="text-xs font-semibold px-3.5 py-2 rounded-full border border-ember/40 text-ember hover:bg-ember/10 transition-colors">Incident</button>}
                <button data-testid={`checkout-${c.id}`} onClick={() => setCheckoutFor(checkoutFor === c.checkin_id ? null : c.checkin_id)} className="text-xs font-semibold px-3.5 py-2 rounded-full bg-ember text-white hover:bg-ember-light transition-colors">Check Out</button>
              </div>
              {notifyFor === c.id && (
                <div className="mt-3 flex flex-wrap gap-2" data-testid={`notify-reasons-${c.id}`}>
                  {NOTIFY_REASONS.map(([v, label]) => (
                    <button key={v} data-testid={`notify-${c.id}-${v}`} onClick={() => sendNotify(c, v)} className="text-xs font-semibold px-3 py-1.5 rounded-full border border-border bg-white text-river hover:border-water transition-colors">{label}</button>
                  ))}
                </div>
              )}
              {notifyMsg?.childId === c.id && (
                <div className="mt-3 rounded-xl bg-white border border-border p-3.5" data-testid={`notify-message-${c.id}`}>
                  <p className="text-xs text-foreground/80 italic">"{notifyMsg.message}"</p>
                  <div className="flex items-center gap-3 mt-2">
                    {notifyMsg.sms_sent ? (
                      <span className="text-xs font-semibold text-water inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Text sent to {notifyMsg.parent_mobile}</span>
                    ) : (
                      <>
                        <button data-testid={`notify-copy-${c.id}`} onClick={copyNotify} className="text-xs font-semibold text-water hover:text-river">Copy text</button>
                        <span className="text-[11px] text-muted-foreground">send to {notifyMsg.parent_mobile}</span>
                      </>
                    )}
                  </div>
                </div>
              )}
              {checkoutFor === c.checkin_id && (
                <div className="mt-3 flex flex-wrap items-center gap-2" data-testid={`checkout-form-${c.id}`}>
                  <input data-testid={`checkout-code-${c.id}`} className="w-28 min-h-[40px] rounded-lg border border-border bg-white px-3 text-sm font-mono outline-none focus:border-water" placeholder="Code" value={checkout.code} onChange={(e) => setCheckout({ ...checkout, code: e.target.value })} />
                  <input data-testid={`checkout-released-${c.id}`} className="flex-1 min-w-[160px] min-h-[40px] rounded-lg border border-border bg-white px-3 text-sm outline-none focus:border-water" placeholder="Released to (name)" value={checkout.released_to} onChange={(e) => setCheckout({ ...checkout, released_to: e.target.value })} />
                  <button data-testid={`checkout-confirm-${c.id}`} onClick={() => doCheckout(c.checkin_id)} disabled={busy} className="text-xs font-semibold px-4 py-2.5 rounded-full bg-ember text-white hover:bg-ember-light disabled:opacity-50 transition-colors">Confirm Check-Out</button>
                </div>
              )}
            </div>
          ))}
        </div>
        {checkedOut.length > 0 && (
          <div className="mt-5 pt-4 border-t border-border/60" data-testid="checked-out-list">
            <p className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Checked out</p>
            {checkedOut.map((c) => (
              <p key={c.checkin_id} className="text-xs text-muted-foreground py-1">
                {c.name} — in {fmtTime(c.checkin_at)}, out {fmtTime(c.checkout_at)}, released to {c.released_to} · {c.checkout_facilitator || c.facilitator}
              </p>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-border p-6 mb-5">
        <h3 className="font-serif text-xl text-river mb-4">Registered Children ({children.length})</h3>
        {children.length === 0 && <p className="text-sm text-muted-foreground">No children registered yet — share the registration link from the connection card.</p>}
        <div className="space-y-2.5" data-testid="registered-children-list">
          {children.map((c) => (
            <div key={c.id} className="flex items-center gap-3 rounded-xl border border-border p-3.5" data-testid={`child-row-${c.id}`}>
              <div className="w-10 h-10 rounded-full bg-water/10 flex items-center justify-center font-serif font-semibold text-water shrink-0">
                {c.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  {isLead ? (
                    <button data-testid={`child-profile-${c.id}`} onClick={() => setProfile(c)} className="font-semibold text-river text-sm hover:text-water transition-colors">{c.name}</button>
                  ) : (
                    <p className="font-semibold text-river text-sm">{c.name}</p>
                  )}
                  <span className="text-xs text-muted-foreground">Age {c.age || "—"}</span>
                  <AlertChips c={c} />
                </div>
                <p className="text-xs text-muted-foreground truncate">{c.parent_name} · {c.parent_mobile}</p>
              </div>
              {expected.some((e) => e.id === c.id) && (
                <button data-testid={`checkin-${c.id}`} onClick={() => checkIn(c.id)} className="shrink-0 text-xs font-semibold px-4 py-2 rounded-full bg-river text-alabaster hover:bg-river-light transition-colors">Check In</button>
              )}
            </div>
          ))}
        </div>
      </div>

      {isLead && incidents !== null && (
        <div className="bg-white rounded-2xl border border-border p-6" data-testid="incidents-section">
          <h3 className="font-serif text-xl text-river mb-1">Incident Reports</h3>
          <p className="text-xs text-muted-foreground mb-4">Restricted — visible only to Children's Ministry leadership and administrators.</p>
          {incidents.length === 0 && <p className="text-sm text-muted-foreground">No incidents filed.</p>}
          <div className="space-y-2.5">
            {incidents.map((i) => (
              <div key={i.id} className="rounded-xl border border-border p-4 text-sm" data-testid={`incident-${i.id}`}>
                <div className="flex justify-between mb-1">
                  <span className="font-semibold text-river">{i.child_name} — {i.type}</span>
                  <span className="text-xs font-mono text-muted-foreground">{fmtDate(i.created_at)}</span>
                </div>
                <p className="text-foreground/80">{i.what_occurred}</p>
                {i.action_taken && <p className="text-xs text-muted-foreground mt-1">Action: {i.action_taken}</p>}
                <p className="text-[11px] text-muted-foreground mt-1">by {i.completed_by}{i.followup_required ? " · follow-up required" : ""}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <AnimatePresence>
        {profile && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-river-deep/50 backdrop-blur-sm flex items-center justify-center p-5" onClick={() => setProfile(null)} data-testid="child-profile-overlay">
            <motion.div initial={{ scale: 0.96, y: 16 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96, y: 16 }} onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-background rounded-3xl p-6 sm:p-8 max-h-[85vh] overflow-y-auto" data-testid="child-profile-panel">
              <div className="flex items-start justify-between mb-5">
                <div>
                  <h3 className="font-serif text-2xl text-river">{profile.full_name}</h3>
                  <p className="text-xs text-muted-foreground">Authorized profile — Children's Ministry personnel only</p>
                </div>
                <button onClick={() => setProfile(null)} className="w-10 h-10 rounded-full hover:bg-parchment flex items-center justify-center"><X className="w-5 h-5" /></button>
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-3 mb-5">
                <ProfileField label="Preferred name" value={profile.name} />
                <ProfileField label="Age / Grade" value={`${profile.age || "—"}${profile.age_grade ? ` · ${profile.age_grade}` : ""}`} />
                <ProfileField label="Date of birth" value={profile.dob} />
                <ProfileField label="Photo permission" value={profile.photo_permission ? "Yes" : "No"} />
                <ProfileField label="Medical consent" value={profile.medical_consent ? "Yes" : "No"} />
                <ProfileField label="Parent relationship to LFCC" value={profile.lfcc_relationship} />
              </div>
              <div className="space-y-3 text-sm">
                {profile.allergies && <div className="rounded-xl bg-ember/5 border border-ember/25 p-3.5"><p className="text-xs font-semibold text-ember mb-0.5">Allergies</p><p>{profile.allergies}</p></div>}
                {profile.dietary && <div className="rounded-xl bg-ember/5 border border-ember/25 p-3.5"><p className="text-xs font-semibold text-ember mb-0.5">Dietary restrictions</p><p>{profile.dietary}</p></div>}
                {profile.medication && <div className="rounded-xl bg-ember/5 border border-ember/25 p-3.5"><p className="text-xs font-semibold text-ember mb-0.5">Medication / emergency considerations</p><p>{profile.medication}</p></div>}
                {profile.support_needs && <div className="rounded-xl bg-water/5 border border-water/25 p-3.5"><p className="text-xs font-semibold text-water mb-0.5">Support needs</p><p>{profile.support_needs}</p></div>}
                {profile.support_helps && <div className="rounded-xl bg-water/5 border border-water/25 p-3.5"><p className="text-xs font-semibold text-water mb-0.5">What helps them feel safe</p><p>{profile.support_helps}</p></div>}
                {profile.other_notes && <div className="rounded-xl bg-parchment/60 border border-border p-3.5"><p className="text-xs font-semibold text-river mb-0.5">Other notes</p><p>{profile.other_notes}</p></div>}
              </div>
              <div className="mt-5 pt-4 border-t border-border text-sm space-y-1.5">
                <p><span className="text-muted-foreground text-xs">Parent / Guardian:</span> <b className="text-river">{profile.parent_name}</b> · {profile.parent_mobile}</p>
                {profile.emergency_contact && <p><span className="text-muted-foreground text-xs">Emergency contact:</span> {profile.emergency_contact}</p>}
                {profile.authorized_pickup?.length > 0 && <p><span className="text-muted-foreground text-xs">Authorized pickup:</span> <b className="text-river">{profile.authorized_pickup.join(", ")}</b></p>}
                {profile.not_authorized && <p className="text-ember"><span className="text-xs">NOT authorized:</span> <b>{profile.not_authorized}</b></p>}
                {profile.instructions && <p><span className="text-muted-foreground text-xs">Parent instructions:</span> {profile.instructions}</p>}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {incidentFor && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-river-deep/50 backdrop-blur-sm flex items-center justify-center p-5" onClick={() => setIncidentFor(null)} data-testid="incident-overlay">
            <motion.div initial={{ scale: 0.96, y: 16 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96, y: 16 }} onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-background rounded-3xl p-6 sm:p-8 max-h-[85vh] overflow-y-auto" data-testid="incident-form">
              <div className="flex items-start justify-between mb-5">
                <div>
                  <h3 className="font-serif text-2xl text-river">Incident Report</h3>
                  <p className="text-xs text-muted-foreground">{incidentFor.name} — restricted record</p>
                </div>
                <button onClick={() => setIncidentFor(null)} className="w-10 h-10 rounded-full hover:bg-parchment flex items-center justify-center"><X className="w-5 h-5" /></button>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <label className="block"><span className="text-xs font-semibold text-river block mb-1">Type</span>
                    <select data-testid="incident-type" className={inputCls} value={incident.type} onChange={(e) => setIncident({ ...incident, type: e.target.value })}>
                      {INCIDENT_TYPES.map((t) => <option key={t}>{t}</option>)}</select></label>
                  <label className="block"><span className="text-xs font-semibold text-river block mb-1">Date / Time</span>
                    <input data-testid="incident-occurred" className={inputCls} placeholder="e.g. Today 11:20 AM" value={incident.occurred_at} onChange={(e) => setIncident({ ...incident, occurred_at: e.target.value })} /></label>
                </div>
                <label className="block"><span className="text-xs font-semibold text-river block mb-1">Staff present</span>
                  <input data-testid="incident-staff" className={inputCls} value={incident.staff_present} onChange={(e) => setIncident({ ...incident, staff_present: e.target.value })} /></label>
                <label className="block"><span className="text-xs font-semibold text-river block mb-1">What occurred</span>
                  <textarea data-testid="incident-what" rows={3} className={`${inputCls} py-2.5 resize-none`} value={incident.what_occurred} onChange={(e) => setIncident({ ...incident, what_occurred: e.target.value })} /></label>
                <label className="block"><span className="text-xs font-semibold text-river block mb-1">Action taken</span>
                  <textarea data-testid="incident-action" rows={2} className={`${inputCls} py-2.5 resize-none`} value={incident.action_taken} onChange={(e) => setIncident({ ...incident, action_taken: e.target.value })} /></label>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block"><span className="text-xs font-semibold text-river block mb-1">Parent contacted at</span>
                    <input data-testid="incident-contacted" className={inputCls} placeholder="e.g. 11:35 AM" value={incident.parent_contacted_at} onChange={(e) => setIncident({ ...incident, parent_contacted_at: e.target.value })} /></label>
                  <label className="block"><span className="text-xs font-semibold text-river block mb-1">Child released to</span>
                    <input data-testid="incident-released" className={inputCls} value={incident.released_to} onChange={(e) => setIncident({ ...incident, released_to: e.target.value })} /></label>
                </div>
                <label className="block"><span className="text-xs font-semibold text-river block mb-1">Parent response</span>
                  <input data-testid="incident-response" className={inputCls} value={incident.parent_response} onChange={(e) => setIncident({ ...incident, parent_response: e.target.value })} /></label>
                <label className="flex items-center gap-2.5 min-h-[44px] cursor-pointer">
                  <input data-testid="incident-followup" type="checkbox" checked={incident.followup_required} onChange={(e) => setIncident({ ...incident, followup_required: e.target.checked })} className="w-4.5 h-4.5 accent-ember" />
                  <span className="text-sm font-medium text-river">Follow-up required</span>
                </label>
                <button data-testid="incident-save" onClick={saveIncident} disabled={busy || !incident.what_occurred.trim()} className="w-full min-h-[48px] rounded-full bg-ember text-white font-semibold text-sm hover:bg-ember-light disabled:opacity-50 transition-colors inline-flex items-center justify-center gap-2">
                  {busy && <Loader2 className="w-4 h-4 animate-spin" />} File Report
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function SundayReview({ onOpen, userRole }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [busy, setBusy] = useState("");
  const [confirming, setConfirming] = useState("");

  const load = useCallback(() => {
    api.get("/admin/sunday-review")
      .then(({ data }) => setData(data))
      .catch((e) => { toast.error(apiError(e)); setData(false); });
  }, []);
  useEffect(load, [load]);

  if (!data) return null;

  const mark = async (id, m) => {
    setBusy(id);
    try {
      await api.post(`/admin/sunday-review/${id}`, { mark: m });
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const remove = async (id, mode) => {
    setBusy(id);
    try {
      await api.post(`/admin/sunday-review/${id}/remove`, { mode });
      toast.success(mode === "duplicate" ? "Duplicate record deleted" : "Removed from Sunday Review");
      setConfirming("");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const people = data.people.filter((p) =>
    (!typeFilter || p.person_type === typeFilter) &&
    (!q || p.name.toLowerCase().includes(q.toLowerCase())));
  const weekLabel = new Date(`${data.week_of}T12:00:00`).toLocaleDateString("en-CA", { month: "long", day: "numeric" });

  return (
    <div className="bg-white rounded-2xl border border-border p-6 mb-5" data-testid="sunday-review">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-1">
        <h3 className="font-serif text-xl text-river">Sunday Review — Who We Didn't See</h3>
        <span className="text-[11px] font-mono text-muted-foreground" data-testid="sr-progress">{data.marked} of {data.total} reviewed · week of {weekLabel}</span>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        After each service, tap through the family list. No check-in lines and nothing on anyone's phone — just leaders noticing, so an absence becomes a caring call, not a quiet drift.
      </p>
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input data-testid="sr-search" placeholder="Search a name…" className={`${inputCls} pl-11`} value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        {["", "Member", "Regular Attendee", "Inactive Member"].map((t) => (
          <button key={t || "all"} data-testid={`sr-filter-${t ? t.toLowerCase().replace(/[^a-z]+/g, "-") : "all"}`} onClick={() => setTypeFilter(t)} className={`min-h-[40px] rounded-full border px-4 text-xs font-semibold transition-colors ${typeFilter === t ? "border-river bg-river text-alabaster" : "border-border bg-white text-river"}`}>{t || "All"}</button>
        ))}
      </div>
      <div className="space-y-2">
        {people.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">Nobody matches.</p>}
        {people.map((p) => (
          <div key={p.id} data-testid={`sr-row-${p.person_id}`} className="flex flex-wrap items-center gap-3 rounded-xl border border-border px-4 py-3">
            <button className="flex-1 min-w-[200px] text-left" onClick={() => onOpen(p.id)} data-testid={`sr-open-${p.person_id}`}>
              <p className="text-sm font-semibold text-river">{p.name} <span className="text-[10px] font-mono text-muted-foreground">{p.person_id} · {p.person_type}</span></p>
              <p className="text-[11px] text-muted-foreground">
                {p.days_away === null ? "No visit date on record" : p.days_away === 0 ? "Seen today" : `Last seen ${p.days_away} days ago`}
                {p.streak >= 2 && <span className="text-ember font-semibold" data-testid={`sr-streak-${p.person_id}`}> · missed {p.streak}{p.streak >= 10 ? "+" : ""} Sundays</span>}
                {p.care && <span className="text-ember font-semibold"> · {p.care}</span>}
                {p.mark && p.marked_by && <span> · marked {p.mark === "seen" ? "seen" : "absent"} by {p.marked_by}</span>}
              </p>
            </button>
            <div className="flex gap-2">
              {confirming === p.id ? (
                <div className="flex flex-wrap items-center gap-2" data-testid={`sr-remove-confirm-${p.person_id}`}>
                  <button data-testid={`sr-remove-inactive-${p.person_id}`} disabled={busy === p.id} onClick={() => remove(p.id, "inactive")} className="min-h-[40px] rounded-full px-4 text-xs font-semibold bg-river text-alabaster hover:bg-river-light transition-colors">
                    No longer active
                  </button>
                  {userRole === "admin" && (
                    <button data-testid={`sr-remove-duplicate-${p.person_id}`} disabled={busy === p.id} onClick={() => remove(p.id, "duplicate")} className="min-h-[40px] rounded-full px-4 text-xs font-semibold bg-destructive text-white inline-flex items-center gap-1.5 hover:opacity-90 transition-opacity">
                      <Trash2 className="w-3.5 h-3.5" /> Delete duplicate
                    </button>
                  )}
                  <button data-testid={`sr-remove-cancel-${p.person_id}`} onClick={() => setConfirming("")} className="min-h-[40px] rounded-full px-3 text-xs font-semibold text-muted-foreground hover:text-river transition-colors">
                    Keep on list
                  </button>
                </div>
              ) : (
                <>
                  <button data-testid={`sr-seen-${p.person_id}`} disabled={busy === p.id} onClick={() => mark(p.id, p.mark === "seen" ? "clear" : "seen")} className={`min-h-[40px] rounded-full px-4 text-xs font-semibold inline-flex items-center gap-1.5 transition-colors ${p.mark === "seen" ? "bg-water text-white" : "border border-border text-water hover:bg-water/10"}`}>
                    <CheckCircle2 className="w-3.5 h-3.5" /> Saw them
                  </button>
                  <button data-testid={`sr-absent-${p.person_id}`} disabled={busy === p.id} onClick={() => mark(p.id, p.mark === "absent" ? "clear" : "absent")} className={`min-h-[40px] rounded-full px-4 text-xs font-semibold inline-flex items-center gap-1.5 transition-colors ${p.mark === "absent" ? "bg-ember text-white" : "border border-border text-ember hover:bg-ember/10"}`}>
                    <AlertTriangle className="w-3.5 h-3.5" /> Didn't see
                  </button>
                  <button data-testid={`sr-remove-${p.person_id}`} disabled={busy === p.id} onClick={() => setConfirming(p.id)} aria-label={`Remove ${p.name} from Sunday Review`} className="min-h-[40px] w-[40px] rounded-full border border-border text-muted-foreground hover:text-destructive hover:border-destructive/40 inline-flex items-center justify-center transition-colors">
                    <X className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


function CareView({ onOpen, userRole }) {
  const [data, setData] = useState(null);
  const [cat, setCat] = useState(null);

  useEffect(() => {
    api.get("/admin/care-dashboard")
      .then(({ data }) => setData(data))
      .catch((e) => { toast.error(apiError(e)); setData(false); });
  }, []);

  if (data === null) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-river" /></div>;
  if (data === false) return null;

  const cats = [
    ["visitors_needing_followup", "Visitors Needing Follow-Up", PhoneCall, "ember"],
    ["regular_absent", "Regular Attendees Absent 3+ Weeks", Users, "river"],
    ["member_absent", "Members Absent 3+ Weeks", AlertTriangle, "ember"],
    ["known_travelling", "Known Travelling", Plane, "water"],
    ["known_sick", "Known Sick / Hospitalized", Stethoscope, "water"],
    ["bereavement", "Bereavement", HeartHandshake, "river"],
    ["pastoral", "Pastoral Follow-Up Required", MailQuestion, "ember"],
    ["overdue", "Follow-Up Overdue", Clock, "ember"],
    ["expected_back", "Expected Back This Week", CalendarCheck2, "water"],
  ];

  const tones = { river: "bg-river/5 text-river", ember: "bg-ember/10 text-ember", water: "bg-water/10 text-water" };
  const selected = cats.find(([k]) => k === cat);

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} data-testid="care-view">
      <div className="bg-white rounded-2xl border border-border p-6 mb-5">
        <h3 className="font-serif text-xl text-river mb-1">Congregational Care</h3>
        <p className="text-xs text-muted-foreground mb-3">
          Known absences stay quiet; unexplained ones surface. {data.type_counts.Member || 0} members · {data.type_counts["Regular Attendee"] || 0} regular attendees · {data.type_counts.Visitor || 0} visitors in the database.
        </p>
        <p className="text-[11px] text-muted-foreground">Members absent 3+ weeks trigger the member-care process; regular attendees get relational check-ins. Record a known absence on any person's profile to clear the flag.</p>
      </div>
      <SundayReview onOpen={onOpen} userRole={userRole} />
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {cats.map(([key, label, Icon, tone]) => (
          <button
            key={key}
            data-testid={`care-cat-${key}`}
            onClick={() => setCat(cat === key ? null : key)}
            className={`text-left bg-white rounded-2xl border p-5 transition-[border-color,box-shadow] hover:shadow-md ${cat === key ? "border-river" : "border-border"}`}
          >
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-3 ${tones[tone]}`}>
              <Icon className="w-4.5 h-4.5" />
            </div>
            <p className="text-2xl font-serif font-semibold text-river">{data[key].length}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
          </button>
        ))}
      </div>
      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
            data-testid="care-cat-list"
          >
            <div className="mt-5 bg-white rounded-2xl border border-border p-6">
              <p className="font-serif text-lg text-river mb-3">{selected[1]}</p>
              {data[selected[0]].length === 0 && <p className="text-sm text-muted-foreground">Nobody here right now — wonderful.</p>}
              <div className="space-y-2">
                {data[selected[0]].map((p) => (
                  <button key={p.id} data-testid={`care-person-${p.person_id}`} onClick={() => onOpen(p.id)} className="w-full text-left flex items-center justify-between rounded-xl border border-border px-4 py-3 hover:border-water transition-colors">
                    <div>
                      <p className="text-sm font-semibold text-river">{p.name} <span className="text-xs font-mono text-muted-foreground">{p.person_id}</span></p>
                      {p.extra && <p className="text-xs text-muted-foreground">{p.extra}</p>}
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function Dashboard({ user, onLogout }) {
  const [stats, setStats] = useState(null);
  const [people, setPeople] = useState([]);
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({ person_type: "", age_range: "", gender: "", source: "", request: "", intention_gap: false, imported: false });
  const [options, setOptions] = useState(null);
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState("overview");

  const load = useCallback(async () => {
    const params = { q, person_type: filters.person_type, age_range: filters.age_range, gender: filters.gender, source: filters.source, request: filters.request, intention_gap: filters.intention_gap, imported: filters.imported ? "true" : "" };
    const [s, p] = await Promise.all([api.get("/admin/stats"), api.get("/admin/people", { params })]);
    setStats(s.data);
    setPeople(p.data);
  }, [q, filters]);

  useEffect(() => {
    api.get("/admin/filter-options").then(({ data }) => setOptions(data)).catch(() => {});
  }, []);

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const exportCsv = async () => {
    const { data } = await api.get("/admin/export", { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "lfcc-visitors.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const tabs = [
    ["overview", "Overview"],
    ["people", `People (${people.length})`],
    ["surveys", "Surveys"],
    ["care", "Care"],
    ...((["admin", "children_lead", "children_volunteer"].includes(user.role)) ? [["children", "Children"]] : []),
    ["reports", "Reports"],
    ...(user.role === "admin" ? [["team", "Team"]] : []),
    ["activity", "Activity"],
    ["print", "Print & QR"],
  ];

  return (
    <div className="min-h-screen bg-background texture-grain" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-alabaster/85 border-b border-border">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo size={40} />
            <div className="leading-tight">
              <p className="font-serif font-semibold text-river">Follow-Up Hub</p>
              <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground" data-testid="signed-in-as">
                {user.name || user.email}{user.role === "admin" ? " · Administrator" : " · Leader"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={exportCsv} data-testid="export-csv-button" className="inline-flex items-center gap-2 min-h-[40px] px-4 rounded-full border border-border text-sm font-semibold text-river hover:bg-parchment transition-colors">
              <Download className="w-4 h-4" /> <span className="hidden sm:inline">Export CSV</span>
            </button>
            <button onClick={onLogout} data-testid="logout-button" className="inline-flex items-center gap-2 min-h-[40px] px-4 rounded-full text-sm font-semibold text-muted-foreground hover:text-destructive transition-colors">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-8">
        <div className="flex gap-2 mb-8 flex-wrap">
          {tabs.map(([id, label]) => (
            <button key={id} data-testid={`view-tab-${id}`} onClick={() => setView(id)} className={`px-5 min-h-[44px] rounded-full text-sm font-semibold transition-colors inline-flex items-center gap-2 ${view === id ? "bg-river text-alabaster" : "bg-white border border-border text-muted-foreground hover:text-river"}`}>
              {id === "team" && <UserCog className="w-4 h-4" />}
              {id === "activity" && <History className="w-4 h-4" />}
              {id === "surveys" && <ClipboardList className="w-4 h-4" />}
              {id === "care" && <HeartHandshake className="w-4 h-4" />}
              {id === "children" && <Baby className="w-4 h-4" />}
              {id === "reports" && <BarChart3 className="w-4 h-4" />}
              {id === "print" && <QrCode className="w-4 h-4" />}
              {label}
            </button>
          ))}
        </div>

        {view === "team" && <TeamView currentUser={user} />}
        {view === "activity" && <ActivityView />}
        {view === "surveys" && <SurveysView />}
        {view === "care" && <CareView onOpen={(id) => setSelected({ id })} userRole={user.role} />}
        {view === "children" && <ChildrenView user={user} />}
        {view === "reports" && <ReportsView onOpen={(id) => setSelected({ id })} />}
        {view === "print" && <PrintView />}

        {view === "people" && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="relative mb-4">
              <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input data-testid="people-search-input" placeholder="Search name, email, phone, ID or invited-by…" className={`${inputCls} pl-11`} value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
            {options && (
              <div className="flex flex-wrap gap-2 mb-5" data-testid="people-filters">
                {[
                  ["person_type", "All types", options.person_types],
                  ["age_range", "All ages", options.age_ranges],
                  ["gender", "Male / Female", options.genders],
                  ["source", "All sources", options.sources],
                  ["request", "Any request", options.requests],
                ].map(([key, placeholder, opts]) => (
                  <select
                    key={key}
                    data-testid={`filter-${key}`}
                    value={filters[key]}
                    onChange={(e) => setFilters({ ...filters, [key]: e.target.value })}
                    className={`min-h-[40px] rounded-full border px-4 text-xs font-semibold outline-none transition-colors ${filters[key] ? "border-river bg-river text-alabaster" : "border-border bg-white text-river"}`}
                  >
                    <option value="">{placeholder}</option>
                    {opts.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ))}
                <button
                  data-testid="filter-intention-gap"
                  onClick={() => setFilters({ ...filters, intention_gap: !filters.intention_gap })}
                  className={`min-h-[40px] rounded-full border px-4 text-xs font-semibold transition-colors ${filters.intention_gap ? "border-ember bg-ember text-white" : "border-border bg-white text-ember"}`}
                >
                  Intended to return · not back in 30 days
                </button>
                <button
                  data-testid="filter-imported"
                  onClick={() => setFilters({ ...filters, imported: !filters.imported })}
                  className={`min-h-[40px] rounded-full border px-4 text-xs font-semibold transition-colors ${filters.imported ? "border-river bg-river text-alabaster" : "border-border bg-white text-river"}`}
                >
                  Imported records only
                </button>
                {(filters.person_type || filters.age_range || filters.gender || filters.source || filters.request || filters.intention_gap || filters.imported) && (
                  <button
                    data-testid="filter-clear"
                    onClick={() => setFilters({ person_type: "", age_range: "", gender: "", source: "", request: "", intention_gap: false, imported: false })}
                    className="min-h-[40px] rounded-full px-4 text-xs font-semibold text-muted-foreground hover:text-destructive transition-colors"
                  >
                    Clear
                  </button>
                )}
              </div>
            )}
            <div className="space-y-2.5" data-testid="people-list">
              {people.length === 0 && <p className="text-sm text-muted-foreground py-8 text-center">No one matches — adjust the filters or search.</p>}
              {people.map((p) => (
                <button key={p.id} data-testid={`person-row-${p.person_id}`} onClick={() => setSelected(p)} className="w-full text-left bg-white rounded-2xl border border-border p-4 hover:border-water hover:shadow-md transition-[border-color,box-shadow] flex items-center gap-4">
                  <div className="w-11 h-11 rounded-full bg-river/5 flex items-center justify-center font-serif font-semibold text-river shrink-0">
                    {p.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-river">{p.name}</p>
                      {p.imported && <span className="text-[10px] font-mono uppercase tracking-wider bg-parchment text-muted-foreground px-2 py-0.5 rounded-full border border-border">Imported</span>}
                      {p.person_type && p.person_type !== "Visitor" && <span className="text-[10px] font-mono uppercase tracking-wider bg-river/10 text-river px-2 py-0.5 rounded-full">{p.person_type}</span>}
                      {p.care && <span className="text-[10px] font-mono uppercase tracking-wider bg-ember/10 text-ember px-2 py-0.5 rounded-full">Care: {p.care.status}</span>}
                      {p.followup_outstanding && <span className="text-[10px] font-mono uppercase tracking-wider bg-ember/10 text-ember px-2 py-0.5 rounded-full">Follow-up needed</span>}
                      {p.is_minor && <span className="text-[10px] font-mono uppercase tracking-wider bg-water/10 text-water px-2 py-0.5 rounded-full">Minor</span>}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">{p.person_id} · {p.stage_label}{p.invited_by ? ` · invited by ${p.invited_by}` : ""}</p>
                  </div>
                  <div className="text-right shrink-0 hidden sm:block">
                    <p className="text-xs font-mono text-muted-foreground">{p.visit_count} visit{p.visit_count === 1 ? "" : "s"}</p>
                    <p className="text-xs text-muted-foreground">{p.last_visit_at?.slice(0, 10)}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                </button>
              ))}
            </div>
          </motion.div>
        )}

        {view === "overview" && (!stats ? (
          <div className="flex justify-center py-24"><Loader2 className="w-7 h-7 animate-spin text-river" /></div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <StatCard testid="stat-first-time" icon={UserPlus} label="First-Time Visitors" value={stats.first_time_visitors} tone="ember" />
              <StatCard testid="stat-returning" icon={Users} label="Returning Visitors" value={stats.returning_visitors} />
              <StatCard testid="stat-followup-done" icon={CheckCircle2} label="Follow-Up Completed" value={stats.followup_completed} tone="water" />
              <StatCard testid="stat-followup-outstanding" icon={Clock} label="Follow-Up Outstanding" value={stats.followup_outstanding} tone="ember" />
              <StatCard testid="stat-second-visits" icon={Users} label="Second Visits" value={stats.second_visits} />
              <StatCard testid="stat-recontact" icon={MailQuestion} label="Need Gentle Re-Contact" value={stats.needs_recontact} tone="ember" />
              <StatCard testid="stat-nbc" icon={Waves} label="New Believers Class Interest" value={stats.nbc_interest} />
              <StatCard testid="stat-invited" icon={PhoneCall} label="Personal Invitations" value={stats.personal_invitations} tone="ember" />
            </div>
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl border border-border p-6" data-testid="source-breakdown">
                <h3 className="font-serif text-xl text-river mb-5">Where Visitors Came From</h3>
                <div className="space-y-3">
                  {stats.sources.length === 0 && <p className="text-sm text-muted-foreground">No visitor data yet.</p>}
                  {stats.sources.map((s) => <Bar key={s.name} name={s.name} value={s.count} max={stats.sources[0]?.count} suffix={`${s.pct}%`} />)}
                </div>
              </div>
              <div className="bg-white rounded-2xl border border-border p-6" data-testid="ministry-interest">
                <h3 className="font-serif text-xl text-river mb-5">Ministry Interest</h3>
                <div className="space-y-3">
                  {stats.ministries.length === 0 && <p className="text-sm text-muted-foreground">No ministry interest yet.</p>}
                  {stats.ministries.map((m) => <Bar key={m.name} name={m.name} value={m.count} max={stats.ministries[0]?.count} />)}
                </div>
              </div>
            </div>
            <SurveyMetrics />
          </motion.div>
        ))}
      </main>

      <AnimatePresence>
        {selected && <PersonDetail person={selected} onClose={() => setSelected(null)} onChanged={load} onSwitch={(id) => setSelected({ id })} />}
      </AnimatePresence>
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    api.get("/auth/me")
      .then(({ data }) => setUser(data))
      .catch(() => setUser(false))
      .finally(() => setChecking(false));
  }, []);

  const logout = async () => {
    await api.post("/auth/logout");
    setUser(false);
  };

  if (checking) {
    return <div className="min-h-screen bg-river-deep flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-alabaster" /></div>;
  }
  if (!user) return <Login onLogin={setUser} resetToken={new URLSearchParams(window.location.search).get("reset")} />;
  return (
    <div>
      <a href="/" data-testid="admin-back-home" className="fixed bottom-4 left-4 z-40 inline-flex items-center gap-2 text-xs font-mono bg-white border border-border rounded-full px-4 py-2.5 text-muted-foreground hover:text-river shadow-lg">
        <ArrowLeft className="w-3.5 h-3.5" /> Visitor page
      </a>
      <Dashboard user={user} onLogout={logout} />
    </div>
  );
}
